import type { SharedContentProgress, UserProfile } from '../types';
import type { ProgramCategory, ProgramPricing } from '../types/program';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { migrateLegacyArtifactSources } from '../shared/migrate-artifact-sources';
import { AuthService } from '@app/auth';

/**
 * Format an HttpErrorResponse from the API into a human-readable message and
 * log the raw error for debugging. Surfaces zod `details` when the backend
 * rejected the request body, so admins see "paymentMethod: Required" instead
 * of just "Invalid request body".
 */
export function formatApiError(err: unknown, fallback = 'Request failed'): string {
  // Always log the raw error so the console has the full picture.
  // eslint-disable-next-line no-console
  console.error('API error', err);

  const body = (err as { error?: unknown })?.error;
  if (body && typeof body === 'object') {
    const top = (body as { error?: string }).error;
    const details = (body as { details?: unknown }).details;
    if (Array.isArray(details) && details.length > 0) {
      const parts = details
        .map((d) => {
          const path = Array.isArray((d as { path?: unknown[] }).path)
            ? (d as { path: unknown[] }).path.join('.')
            : '';
          const message = (d as { message?: string }).message ?? 'invalid';
          return path ? `${path}: ${message}` : message;
        })
        .filter(Boolean);
      const head = top ? `${top} — ` : '';
      return `${head}${parts.join('; ')}`;
    }
    if (top) return top;
  }
  return (err as { message?: string })?.message ?? fallback;
}
import type {
  Conversation,
  CreateDiscountCodeRequest,
  BundleItem,
  CreateProductRequest,
  Decision,
  DiscountCode,
  DiscountCodeWithProduct,
  DiscountRedemption,
  Division,
  Feedback,
  FeedbackListItem,
  ListProductsFilters,
  Memory,
  Message,
  Organization,
  OrganizationKind,
  ParticipantListItem,
  UserDirectoryItem,
  Product,
  SeatBundle,
  Program,
  ProgramFlow,
  ProgramModule,
  ProgramProgress,
  StoredArtifact,
  UpdateDiscountCodeRequest,
  UpdateOrganizationSettingsRequest,
  UpdateProductRequest,
  User,
  UserFile,
} from '@app/types';

export interface ListFeedbackParams {
  limit?: number;
  cursor?: string;
}

export interface ListFeedbackResponse {
  feedback: FeedbackListItem[];
  nextCursor: string | null;
}
import { environment } from '../../environments/environment';
import type { OrgPurgePlan, OrgPurgeSummary } from '../types/org-purge';

export interface ListUsersParams {
  limit?: number;
  cursor?: string;
  search?: string;
  enrolledOnly?: boolean;
}

export interface ListUsersResponse {
  users: ParticipantListItem[];
  nextCursor: string | null;
}

export interface UserDirectoryResponse {
  users: UserDirectoryItem[];
  nextCursor: string | null;
}

export interface ProgramListItem {
  id: string;
  title: string;
  description: string;
  version?: string;
  status: 'active' | 'archived';
  moduleCount: number;
  latestVersion?: number;
  defaultVersion?: number | null;
  /**
   * 'global' for the two programs that render Program Foundation and
   * Profile. They are content about the person, never a run, never in the
   * library, never comped or priced. Everything else is 'library'.
   */
  kind: 'library' | 'global';
  /** What an additional run costs. Absent until priced. */
  pricing?: ProgramPricing;
  /** Core VentureOff program. Always false on global content. */
  core: boolean;
  /** Which shelf it sits on. `kind` is 'library' exactly when this is 'library'. */
  category: ProgramCategory;
  /** Who owns it. Absent for VentureOff programs. */
  ownerScope?: { type: string; partnerId?: string; divisionId?: string };
  /** Set on copies. */
  forkedFrom?: { programId?: string } | string | null;
}

/** Organization library (white-label T21). */
export interface LibraryRow { programId: string; hidden?: boolean; divisionIds?: string[]; order?: number; featured?: boolean; updatedAt: string; updatedBy: string }
export interface EffectiveLibraryEntry { programId: string; title: string; owner: 'ventureoff' | 'organization' | 'division'; source: 'platform' | 'organization' | 'division'; order: number; featured: boolean }
export interface LibraryOrgUpdate { hidden?: boolean; divisionIds?: string[] | null; order?: number | null; featured?: boolean }
export interface LibraryDivisionUpdate { hidden?: boolean; order?: number | null; featured?: boolean }
export interface LibraryDuplicateResult { program: { id: string; title: string; ownerScope?: { type: string; partnerId?: string; divisionId?: string }; forkedFrom?: unknown } }

export interface CreateProgramRequest {
  title: string;
  description: string;
  /** Omit to leave the price alone, null to clear it. */
  pricing?: ProgramPricing | null;
  /** Omit to leave it alone. */
  core?: boolean;
  /** Omit to leave it alone. Only for programs other than the live Foundation and Profile. */
  category?: 'library' | 'foundation';
}

export type UpdateProgramRequest = CreateProgramRequest;

/** One row of a participant's run-grant ledger, as the admin API returns it. */
export interface AdminRunGrant {
  id: string;
  programId: string;
  programTitle: string;
  source: 'seat' | 'purchase' | 'comp' | 'migration';
  orderId?: string;
  amountPaid?: number;
  currency?: string;
  grantedAt: string;
  grantedBy?: string;
  reason?: string;
  consumedByRunId?: string;
  consumedAt?: string;
  spent: boolean;
}

export type ProgramStatus = 'active' | 'archived';

// --- Program versioning & branching (Program Versioning and Drafts.md §4, §9) ---

export type ProgramVersionStatus = 'published' | 'archived';

export interface ProgramVersionMeta {
  versionNumber: number;
  label?: string;
  status: ProgramVersionStatus;
  parentVersion: number | null;
  mergedFromBranch?: string;
  notes?: string;
  publishedAt: string;
  publishedBy: string;
}

export interface ProgramVersion extends ProgramVersionMeta {
  content: ProgramModule[];
}

export type BranchStatus = 'open' | 'in_review' | 'merged' | 'abandoned';

export interface ProgramBranchMeta {
  branchId: string;
  name: string;
  baseVersion: number;
  status: BranchStatus;
  ownerUid: string;
  rev: number;
  /** True when the branch has a parked update-from-main with unresolved conflicts. */
  hasPendingMerge?: boolean;
  reviewRequestedBy?: string;
  reviewRequestedAt?: string;
  mergedAsVersion?: number;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

/** A prerequisite a participant must meet before opening a program (white-label T23). */
export type ProgramRequirement = { kind: 'program'; programId: string } | { kind: 'profile-answered' };

/** One problem the API found in a branch's requirements (422 `requirementIssues`). */
export interface RequirementIssue { code: string; message: string; programId?: string; path?: string }

export interface ProgramBranch extends ProgramBranchMeta {
  content: ProgramModule[];
  /** Explicit requirements. Absent means the program uses the first-step prerequisite block. */
  requirements?: ProgramRequirement[];
  pendingMerge?: { targetVersion: number; conflicts: MergeConflict[] };
}

export interface PublishBranchResult {
  program: Program;
  version: ProgramVersion;
  promoted: boolean;
  sizeWarning?: string;
}

// --- 3-way merge / conflict resolution (Program Versioning and Drafts.md §6) ---

export interface MergeConflictPath {
  moduleId: string;
  stepId?: string;
  blockId?: string;
  field?: string;
}

export type MergeConflictKind = 'edit-edit' | 'edit-delete' | 'order';

export interface MergeConflict {
  path: MergeConflictPath;
  kind: MergeConflictKind;
  ours: unknown;
  theirs: unknown;
  base: unknown;
}

export interface MergeResolutionInput {
  path: MergeConflictPath;
  kind: MergeConflictKind;
  resolution: 'ours' | 'theirs' | 'manual';
  manualValue?: unknown;
}

export type UpdateFromMainResult =
  | { status: 'up_to_date'; baseVersion: number; latestVersion: number }
  | { status: 'merged'; branch: ProgramBranch }
  | { status: 'conflicts'; targetVersion: number; conflicts: MergeConflict[] };

export interface CreateOrganizationRequest {
  name: string;
  ownerName: string;
  ownerEmail: string;
  kind?: OrganizationKind;
  /** Create the owner invitation without emailing it. Send it later with resendAdminInvitation. */
  deferInvitationEmail?: boolean;
}

export interface CreateOrganizationResponse {
  organization: Organization;
  division: Division;
  invitation: Invitation;
}

export type InvitableMemberRole =
  | 'org_admin'
  | 'org_billing'
  | 'division_owner'
  | 'division_admin'
  | 'division_billing';

export interface InviteMemberRequest {
  email: string;
  name: string;
  role: InvitableMemberRole;
  divisionId?: string;
  /** Extra org-tier permission atoms to grant on top of the role, applied on accept. */
  permissions?: string[];
}

export interface InviteMemberResponse {
  invitation: Invitation;
  userId: string;
  userCreated: boolean;
}

export interface CreateDivisionRequest {
  name: string;
}

/** Where an organization's MSA comes from. */
export type MsaSource = 'organization' | 'standard' | 'placeholder';

export interface EffectiveMsa {
  source: MsaSource;
  version: string;
  fileName: string;
  sha256: string;
  size: number;
}

export interface MsaSignatureSummary {
  method: 'in_app' | 'recorded';
  source: MsaSource;
  version: string;
  signerName: string;
  signerTitle?: string;
  reference?: string;
  signedAt: string;
}

export interface OrgMsaStatus {
  document: EffectiveMsa;
  signed: boolean;
  signature: MsaSignatureSummary | null;
}

export interface MarkSetupFeePaidRequest {
  amount?: number;
  currency?: string;
  method?: string;
  notes?: string;
}

export interface MarkSetupFeePaidResponse {
  division: Division;
  payment: Payment | null;
}

export interface OrganizationListItem {
  id: string;
  name: string;
  status: 'pending_payment' | 'active' | 'archived';
  ownerUserId: string;
  ownerEmail: string;
  ownerSignedIn: boolean;
  /** VOFF-297: who created the org and who runs it. Omitted for customer viewers. */
  origin?: 'platform' | 'self';
  managedBy?: 'ventureoff' | 'self';
  createdAt: string;
  /** Divisions in the organization; for a customer, the ones they can see. */
  divisionCount?: number;
}

export type MembershipRole =
  | 'org_owner'
  | 'org_admin'
  | 'org_billing'
  | 'division_owner'
  | 'division_admin'
  | 'division_billing';

export interface MembershipWithUser {
  id: string;
  userId: string;
  orgId: string;
  divisionId?: string;
  role: MembershipRole;
  grantedBy: string;
  email: string;
  name?: string;
  signedIn: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationDetail {
  access: { organizationMembers: boolean; organizationBilling: boolean; divisionMemberIds: string[]; divisionBillingIds: string[]; participantDetails: boolean };
  organization: Organization;
  divisions: Division[];
  pendingInvitations: Invitation[];
  payments: Payment[];
  /** Invoice-mode purchases, newest first, with the derived overdue flag. Empty for division-only viewers. */
  invoices: Invoice[];
  ownerEmail: string;
  ownerSignedIn: boolean;
  memberships: MembershipWithUser[];
}

export type InvoiceKind = 'participant_roster' | 'seat_renewal' | 'custom';
export type InvoiceStatus = 'draft' | 'open' | 'paid' | 'void';

export interface InvoiceLine {
  description: string;
  amountCents: number;
  quantity: number;
}

export interface InvoiceDraftInput {
  divisionId?: string;
  description?: string;
  lines: InvoiceLine[];
  recipientEmail?: string;
  netTermsDays?: number;
  dueOnReceipt?: boolean;
  memo?: string;
}

/** An org-billing-mode `invoice` purchase (VOFF-270). Mirror of vo-api types/invoice.ts. */
export interface Invoice {
  id: string;
  orgId: string;
  divisionId?: string;
  kind: InvoiceKind;
  targetId: string;
  description: string;
  amountCents: number;
  outstandingCents?: number;
  currency: string;
  seatCount?: number;
  billingEmail?: string;
  lines?: InvoiceLine[];
  netTermsDays: number;
  dueOnReceipt?: boolean;
  memo?: string;
  sentAt?: string;
  dueAt?: string;
  status: InvoiceStatus;
  paidAt?: string;
  paidBy?: string;
  paidNote?: string;
  voidedAt?: string;
  stripe?: {
    invoiceId: string;
    number?: string;
    hostedInvoiceUrl?: string;
    invoicePdf?: string;
    status?: string;
    sentAt: string;
    lastSentAt?: string;
  };
  lastPaymentFailure?: { at: string; reason?: string };
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** Derived server-side: open and past due. */
  overdue?: boolean;
}

/** A restricted product assigned to an org (every division) or one division. */
export interface CatalogAssignment {
  id: string;
  productId: string;
  orgId: string;
  divisionId?: string;
  createdBy: string;
  createdAt: string;
  orgName?: string;
  divisionName?: string;
  /** Present on the org-page listing. */
  product?: {
    id: string;
    displayName: string;
    category: string;
    amountCents: number;
    status: string;
    durationDays?: number;
  } | null;
}

export type SettlementMethod = 'stripe' | 'invoice' | 'comped';

export interface SeatRenewalRecord {
  id: string;
  at: string;
  days: number;
  amountCents: number;
  settlement: SettlementMethod;
  paymentId?: string;
  invoiceId?: string;
  by: string;
  accessEndsAt?: string;
}

export interface RenewSeatResponse {
  seat: Seat;
  days: number;
  amountCents: number;
  clientSecret?: string;
  paymentId?: string;
  renewal?: SeatRenewalRecord;
  invoice?: Invoice;
  comped?: boolean;
  invoiced?: boolean;
  savedPaymentMethods?: BillingAccountPaymentMethod[];
  defaultPaymentMethodId?: string;
  saved?: SavedChargeOutcome;
}

export interface Invitation {
  id: string;
  orgId: string;
  divisionId?: string;
  email: string;
  name?: string;
  /** Legacy role invitations and owner transfers. */
  role?: MembershipRole;
  /** New-model invitations (VOFF-350): the access granted on acceptance. */
  assignment?: { divisionId: string | null; full: boolean; levels: Record<string, string> };
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  sentAt: string;
  /** Created without an email. Resend sends it. */
  emailDeferred?: boolean;
  lastResentAt?: string;
  acceptedAt?: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface Payment {
  id: string;
  orgId: string;
  amount: number;
  currency: string;
  method: string;
  status: string;
  notes?: string;
  recordedBy?: string;
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Participant {
  id: string;
  orgId: string;
  divisionId: string;
  name: string;
  email: string;
  status: 'invited' | 'accepted' | 'active' | 'completed';
  userId?: string;
  invitedAt: string;
  acceptedAt?: string;
  completedAt?: string;
  invitedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ActivateOrgRequest {
  amount?: number;
  currency?: string;
  method?: string;
  paidAt?: string;
  notes?: string;
}

export interface ArchiveOrgRequest {
  reason: string;
}

export interface AddParticipantRequest {
  divisionId: string;
  name: string;
  email: string;
}

export interface BulkAddParticipantsResponse {
  added: number;
  errors: Array<{ row: number; email: string; reason: string }>;
  participants: Participant[];
}

// ---- Transition Roster (P2 participant seats) ----

export type SeatStatus = 'open' | 'assigned';

export interface SeatPackageSnapshot {
  productId: string;
  category: string;
  displayName: string;
  amountCents: number;
  currency: string;
  description: string;
  durationDays?: number;
}

export interface SeatLineItemSnapshot {
  productId: string;
  category: string;
  displayName: string;
  amountCents: number;
  currency: string;
  description: string;
  quantity: number;
  /** Days of program access this line grants, frozen at purchase. Packages only. */
  durationDays?: number;
}

/** A resolved, rule-valid single-seat package: breakdown + headline + summed price. */
export interface SeatConfig {
  package: SeatPackageSnapshot;
  lineItems: SeatLineItemSnapshot[];
  amountCents: number;
}

/** One selected catalog entry for a seat (base, upgrade, or add-on). */
export interface SeatItemInput {
  productId: string;
  quantity?: number;
}

/**
 * Enrollment asks, Purchasing commits, for one more term on one seat
 * (VOFF-360). The seat's email and name are the narrow reconciliation
 * exception: the purchaser pays for a named renewal, and sees nothing of the
 * person's work.
 */
export interface SeatRenewalPurchase {
  id: string;
  status: PurchaseRequestStatus;
  days: number;
  amountCents: number;
  /** Enrollment chose to renew past the seat's sponsorship end date. */
  overrideSponsorship: boolean;
  /** Where the window would end, when the request was made. */
  wouldEndOn?: string;
  seatEmail?: string;
  seatName?: string;
  /** The participant's own renewal request this answers, if any. */
  renewalRequestId?: string;
  requestedBy: { uid: string; email?: string };
  requestedAt: string;
  note?: string;
  /** Customer purchasers told about the request. */
  notifiedUserIds: string[];
  approval?: { by: string; email?: string; at: string; method: 'card' | 'saved' | 'invoice' | 'comped' | 'pilot' };
  decidedBy?: string;
  decidedAt?: string;
  declineNote?: string;
  supersededAt?: string;
  lastFailure?: { reason: string; at: string };
}

export interface Seat {
  id: string;
  orgId: string;
  divisionId: string;
  status: SeatStatus;
  package: SeatPackageSnapshot;
  lineItems: SeatLineItemSnapshot[];
  amountCents: number;
  programId: string;
  email?: string;
  name?: string;
  /** YYYY-MM-DD. The org's seatEligibility label names it (e.g. Graduation date). */
  sponsorshipEndsAt?: string;
  assignedAt?: string;
  inviteSentAt?: string;
  expiresAt?: string;
  claimedByUserId?: string;
  claimedAt?: string;
  /** The purchaser's order view says only whether the seat is claimed, never by whom (VOFF-358). */
  claimed?: boolean;
  rosterId: string;
  /** `org` (default) or `self` for a seat the person bought through an enrollment link. */
  funding?: 'org' | 'self' | 'pilot';
  renewals?: SeatRenewalRecord[];
  pendingRenewalPaymentId?: string;
  /** Latest participant renewal request, mirrored for the seat list. */
  renewalRequest?: { id: string; status: 'requested' | 'fulfilled' | 'declined'; requestedAt: string };
  /** Enrollment's request to buy a renewal for this seat (VOFF-360). */
  renewalPurchase?: SeatRenewalPurchase;
  /** The last failed renewal payment, while it can still be retried. */
  renewalPaymentFailure?: { reason: string; at: string };
  /** Order view only (purchaser-only reader): a renewal payment is in progress. */
  renewalPaymentInProgress?: boolean;
  /** What the seat grants, snapshotted when it was bought. Older seats have none. */
  bundle?: SeatBundle;
  createdAt: string;
  updatedAt: string;
}

/** Staff change to an unclaimed seat's bundle. Removals need `purchaserAgreed`. */
export interface AmendSeatBundleRequest {
  add?: BundleItem[];
  removeProgramIds?: string[];
  reason: string;
  purchaserAgreed?: boolean;
}

/** A participant asking their org for another term (VOFF-266/268). */
export interface RenewalRequest {
  id: string;
  orgId: string;
  divisionId: string;
  seatId: string;
  userId: string;
  email?: string;
  name?: string;
  currentAccessEndsAt?: string;
  days: number;
  amountCents: number;
  status: 'requested' | 'fulfilled' | 'declined';
  requestedAt: string;
  decidedAt?: string;
  decidedBy?: string;
  note?: string;
  renewalId?: string;
}

export type RosterStatus = 'building' | 'awaiting_payment' | 'paid' | 'canceled';

export interface RosterDiscount {
  type: 'percentage' | 'fixed';
  value: number;
  appliedBy: string;
  appliedAt: string;
}

export interface RosterSeatDraft {
  id: string;
  email?: string;
  name?: string;
  sponsorshipEndsAt?: string;
  package: SeatPackageSnapshot;
  lineItems: SeatLineItemSnapshot[];
  amountCents: number;
  programId: string;
}

/** Enrollment asks, Purchasing commits (VOFF-359). A request fixes one roster revision. */
export type PurchaseRequestStatus =
  | 'requested'
  | 'approved'
  | 'fulfilled'
  | 'superseded'
  | 'withdrawn'
  | 'declined';

/** One order line: seats that share a package configuration. */
export interface PurchaseRequestLine {
  items: Array<{ productId: string; displayName: string; quantity: number }>;
  seats: number;
  unitCents: number;
  amountCents: number;
}

export interface PurchaseRequest {
  id: string;
  status: PurchaseRequestStatus;
  /** The roster revision this request fixes. */
  revision: number;
  divisionId: string;
  seatCount: number;
  lines: PurchaseRequestLine[];
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
  currency: 'usd';
  requestedBy: { uid: string; email?: string };
  requestedAt: string;
  note?: string;
  /** Customer purchasers told about the request. */
  notifiedUserIds: string[];
  approval?: { by: string; email?: string; at: string; method: 'card' | 'saved' | 'invoice' | 'comped' | 'pilot' };
  decidedBy?: string;
  decidedAt?: string;
  declineNote?: string;
  supersededAt?: string;
  /** The last failed payment, when a payment failed and the request went back to waiting. */
  lastFailure?: { reason: string; at: string };
}

/** What the caller may do with a roster: build it, pay for it, both or neither (VOFF-359). */
export interface RosterViewer {
  enrollmentManage: boolean;
  purchasesCommit: boolean;
  /** Staff pilot, comp or invoice authority here. Never charges a card. */
  staffSettle?: boolean;
}

/** One row in a division's roster picker (GET /rosters?scope=all). */
export interface RosterSummary {
  id: string;
  /** Absent reads as the purchase date. */
  name?: string;
  status: RosterStatus;
  createdAt: string;
  paidAt?: string;
  seatCount: number;
  claimedCount: number;
}

export interface TransitionRoster {
  id: string;
  orgId: string;
  divisionId: string;
  status: RosterStatus;
  name?: string;
  /** End date (YYYY-MM-DD) new seats start with. */
  defaultSponsorshipEndsAt?: string;
  default: SeatConfig;
  seats: RosterSeatDraft[];
  discount?: RosterDiscount;
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  paymentId?: string;
  paidAt?: string;
  seatIds?: string[];
  settlement?: SettlementMethod | 'pilot';
  invoiceId?: string;
  /** A bank debit is settling; seats appear when the succeeded webhook lands. */
  paymentProcessing?: { method: string; since: string };
  lastPaymentFailure?: { reason: string; at: string };
  /**
   * Commercial revision (VOFF-359). Bumps whenever seats, packages, prices or
   * the discount change, never for names, emails or dates. Absent on rosters
   * made before it existed, read as 0.
   */
  revision?: number;
  /** The latest purchase request for this roster. Earlier ones live in the audit log. */
  purchaseRequest?: PurchaseRequest;
}

/**
 * One entry in "Purchases awaiting approval". A roster order shows lines and
 * prices, never participant names. A renewal names the one seat it renews,
 * the narrow reconciliation exception (VOFF-360).
 */
export type AwaitingPurchase =
  | {
      kind: 'roster';
      rosterId: string;
      divisionId: string;
      divisionName: string;
      rosterStatus: RosterStatus;
      request: PurchaseRequest;
    }
  | {
      kind: 'renewal';
      seatId: string;
      divisionId: string;
      divisionName: string;
      request: SeatRenewalPurchase;
    };

export interface RosterSeatInput {
  /** null clears it (the seat becomes open). */
  email?: string | null;
  name?: string | null;
  /** YYYY-MM or YYYY-MM-DD; null clears (draft edits only). */
  sponsorshipEndsAt?: string | null;
  items?: SeatItemInput[];
}

export interface RosterDiscountInput {
  type: 'percentage' | 'fixed';
  value: number;
}

export interface BulkRosterSeatsResponse {
  roster: TransitionRoster;
  added: number;
  errors: Array<{ row: number; email: string; reason: string }>;
}

/** A saved card or bank account on an org billing account (display only; VOFF-299). */
export interface BillingAccountPaymentMethod {
  paymentMethodId: string;
  type?: 'card' | 'us_bank_account';
  brand: string;
  last4: string;
  expMonth?: number;
  expYear?: number;
  attachedAt: string;
  /** Which divisions may pay with it (VOFF-332). Opt-in: none by default. */
  sharedWith?: { all: boolean; divisionIds: string[] };
}

export interface BillingAccountView {
  id: string;
  displayName: string;
  billingEmail?: string;
  defaultPaymentMethodId?: string;
  paymentMethods: BillingAccountPaymentMethod[];
}

/** What a server-side charge with a saved method came back as (VOFF-299). */
export interface SavedChargeOutcome {
  status: 'succeeded' | 'processing' | 'requires_action';
  clientSecret?: string;
  paymentMethod: BillingAccountPaymentMethod;
}

export interface CheckoutRosterResponse {
  roster: TransitionRoster;
  clientSecret?: string;
  paymentId?: string;
  billingAccountId?: string;
  comped?: boolean;
  /** Free pilot division: comped by rule. */
  pilot?: boolean;
  invoiced?: boolean;
  invoice?: Invoice;
  processing?: { method: string; since: string };
  savedPaymentMethods?: BillingAccountPaymentMethod[];
  defaultPaymentMethodId?: string;
  saved?: SavedChargeOutcome;
}

export interface RegisterOrganizationRequest {
  organizationName: string;
}

export interface RegisterOrganizationResponse {
  organizationId: string;
}

/** Platform-tier roles a super admin may grant/revoke on the Manage User page. */
export type PlatformRoleName =
  | 'super_admin'
  | 'platform_admin'
  | 'platform_billing'
  | 'program_author'
  | 'developer';

export type RoleScopeTier = 'platform' | 'organization' | 'division' | 'program';

/** A single role assignment, enriched with a display label by the API. */
export interface UserRoleAssignment {
  id: string;
  userId: string;
  role: string;
  scopeTier: RoleScopeTier;
  scope: string;
  orgId?: string;
  divisionId?: string;
  programId?: string;
  status?: string;
  archivedAt?: string;
  /** Title (program) or name (org · division) of the scope target. */
  scopeLabel?: string;
  /** True for platform-tier rows — only these are editable from this screen. */
  editable: boolean;
}

export interface UserRolesResponse {
  roles: UserRoleAssignment[];
  platformRoles: PlatformRoleName[];
}

/** A program the user is assigned to, with its archive state. */
export interface UserProgramAssignment {
  programId: string;
  title: string;
  status: 'active' | 'archived';
  roleId: string;
  participantStatus?: string;
  archivedAt?: string;
  /** The version this participant is pinned to. */
  enrolledAtVersion?: number;
  latestVersion?: number;
  defaultVersion?: number | null;
}

export interface UserProgramsResponse {
  programs: UserProgramAssignment[];
}

/** Dry-run / committed re-pin reconciliation result (Program Versioning §8.5). */
export interface ReconciliationSummary {
  from: number;
  to: number;
  matched: number;
  orphaned: number;
  empty: number;
  invalidChoices: number;
  stepsNowIncomplete: string[];
  affectedEnrollments?: number;
}

export interface CohortScope {
  orgId?: string;
  divisionId?: string;
}

/** Permission atom catalog + role bundles, grouped by tier (from code). */
export interface PermissionCatalog {
  permissions: {
    platform: string[];
    organization: string[];
    division: string[];
    program: string[];
  };
  roles: Record<string, string[]>;
}

/** A direct ("inline") permission grant on a single user at a single scope. */
export interface PermissionGrant {
  id: string;
  userId: string;
  permission: string;
  scopeTier: RoleScopeTier;
  scope: string;
  orgId?: string;
  divisionId?: string;
  programId?: string;
  grantedBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Platform-granted curriculum authoring (T22). Staff only. */
export interface CurriculumAuthorGrant {
  id: string;
  userId: string;
  orgId: string;
  divisionId: string | null;
  grantedBy: string;
  grantedAt: string;
  reason: string;
  archivedAt?: string;
  archivedBy?: string;
  archiveReason?: string;
}

/** Team & access (VOFF-350/357). */
export type ResponsibilityRow = 'enrollment' | 'purchasing' | 'billing' | 'participants' | 'coaching' | 'access';

export interface TeamAssignment {
  id: string;
  divisionId: string | null;
  divisionName: string | null;
  full: boolean;
  levels: Partial<Record<ResponsibilityRow, string>>;
  preset: string;
  source: 'invitation' | 'direct' | 'migration' | 'legacy-role';
  legacy: boolean;
  extra: boolean;
  inherited: boolean;
  grantedBy: string;
  grantedByEmail: string;
  grantedAt: string;
  version: number;
  editable: boolean;
}

export interface TeamMember {
  userId: string;
  email: string;
  name?: string;
  signedIn: boolean;
  owner: boolean;
  assignments: TeamAssignment[];
}

export interface TeamAccessResponse {
  /** Authoritative per-scope delegation choices from the API's write-path policy. */
  grantable: Record<string, { appoint: boolean; rows: Partial<Record<ResponsibilityRow, string[]>> }>;
  organization: { id: string; name: string; ownerUserId: string };
  viewer: {
    uid: string;
    manageOrganization: boolean;
    manageDivisionIds: string[];
    appointAdministrators: boolean;
    /** The Owner by the engine's rule. */
    owner: boolean;
    staff: boolean;
    /** The Owner, or staff recovering the organization for them. */
    transferOwnership: boolean;
    ceiling: string | null;
    /** Coaching is offered only for university (education) organizations. */
    coachingAvailable: boolean;
  };
  /** What Administrator gained since the Owner last acknowledged (D-8). Owner only. */
  ownerNotice: { version: number; changes: Array<{ version: number; date: string; summary: string }> } | null;
  divisions: Array<{ id: string; name: string }>;
  members: TeamMember[];
  /** Platform-granted curriculum authors, read-only for customers (T22). */
  curriculumAuthors?: Array<{ userId: string; divisionId: string | null; grantedAt: string }>;
  invitations: Invitation[];
  catalog: {
    responsibilities: Record<ResponsibilityRow, string[]>;
    order: ResponsibilityRow[];
    presets: Array<{ id: string; label: string; full: boolean; levels: Partial<Record<ResponsibilityRow, string>> }>;
  };
  /**
   * VentureOff coaches covering this organization's divisions (VOFF-403). One
   * row per covered division. A customer viewer never sees who the person is
   * (name or email); a staff viewer (`viewer.staff`) also gets those fields.
   */
  ventureoffCoaches: Array<{
    divisionId: string;
    divisionName: string;
    label: string;
    assignmentId?: string;
    userId?: string;
    email?: string;
    name?: string;
  }>;
}

/** Response for GET /admin/organizations/:id/ventureoff-coaches (VOFF-403). */
export interface OrganizationVentureoffCoachesResponse {
  /** False for non-university organizations; coaching is not offered there. */
  coachingAvailable: boolean;
  coaches: Array<{
    assignmentId: string;
    divisionId: string;
    divisionName: string;
    userId: string;
    email: string;
    name?: string;
    grantedAt: string;
  }>;
}

export interface TeamInviteBody {
  email: string;
  name?: string;
  divisionId: string | null;
  full: boolean;
  levels?: Partial<Record<ResponsibilityRow, string>>;
}

export interface OwnerTransferBody {
  email: string;
  name: string;
  keepPreviousOwner: boolean;
  /** Required when staff transfer on the Owner's behalf. */
  reason?: string;
}

export interface TeamAccessHistoryEntry {
  id: string;
  action: string;
  actorEmail: string;
  /** Server-redacted issuer label for invitation acceptance; never an invitation secret. */
  invitedByLabel?: string;
  targetUserId: string | null;
  divisionId: string | null;
  performedAt: string;
  metadata: Record<string, unknown>;
}

/** VentureOff staff assignments (VOFF-355). */
export interface StaffAssignmentView {
  id: string;
  userId: string;
  email: string;
  orgId: string | null;
  organizationName: string | null;
  /** Set only for a VentureOff coach assignment (VOFF-403). Null otherwise. */
  divisionId: string | null;
  divisionName: string | null;
  full: boolean;
  levels: Record<string, string>;
  extras: string[];
  preset: string;
  source: 'direct' | 'migration' | 'legacy-role';
  legacy: boolean;
  grantedBy: string;
  grantedByEmail: string;
  grantedAt: string;
  version: number;
}

/** One university division open for a new VentureOff coach assignment (VOFF-403). */
export interface CoachingDivisionOption {
  orgId: string;
  organizationName: string;
  divisionId: string;
  divisionName: string;
}

export interface StaffAssignmentsResponse {
  assignments: StaffAssignmentView[];
  catalog: {
    responsibilities: Record<string, string[]>;
    order: string[];
    extras: string[];
    presets: Array<{ id: string; label: string; full: boolean; levels: Record<string, string>; extras: string[] }>;
  };
}

export interface StaffAssignmentBody {
  email: string;
  orgId: string | null;
  /** Set only when granting a VentureOff coach assignment (VOFF-403). */
  divisionId?: string | null;
  full: boolean;
  levels?: Record<string, string>;
  extras?: string[];
}

/** Exceptional staff access types a super admin can issue (VOFF-348). */
export type StaffAccessPermission = 'platform.participants.private.read' | 'platform.users.reset';

export interface StaffAccessGrant {
  id: string;
  userId: string;
  userEmail?: string;
  permission: StaffAccessPermission | 'platform.impersonate';
  reason: string;
  grantedBy: string;
  grantedByEmail?: string;
  createdAt: string;
  expiresAt: string | null;
  archivedAt?: string;
  archivedBy?: string | null;
  status: 'active' | 'expired' | 'revoked';
}

export interface StaffAccessGrantsResponse {
  grants: StaffAccessGrant[];
  issuable: StaffAccessPermission[];
  maxHours: number;
}

export type ParticipantAccessReason = 'support' | 'verify_insight' | 'qa' | 'safety' | 'incident';

export interface ParticipantAccessSession {
  sessionId: string;
  reason: ParticipantAccessReason;
  note?: string | null;
  source?: string;
  openedAt: string;
  expiresAt: string;
}

export interface MyStaffAccessResponse {
  staff: boolean;
  superAdmin: boolean;
  platformRoles: string[];
  exceptionalGrants: StaffAccessGrant[];
  maxHours: number;
}

export interface IssueStaffAccessBody {
  email: string;
  permission: StaffAccessPermission;
  reason: string;
  durationHours: number;
}

export interface PermissionGrantsResponse {
  grants: PermissionGrant[];
}

export interface GrantPermissionBody {
  permission: string;
  scopeTier: RoleScopeTier;
  orgId?: string;
  divisionId?: string;
  programId?: string;
}

export interface OrgParticipant {
  progressSummary?: import('../types').ParticipantProgressSummary;
  userId: string;
  divisionId?: string;
  email: string;
  name?: string;
  signedIn: boolean;
  roleId: string;
  createdAt: string;
}

/** Result of uploading an admin-supplied program asset (File block, download mode). */
export interface ProgramAssetUploadResult {
  url: string;
  path: string;
  size: number;
  mimeType: string;
  originalName: string;
}

// ---- Admin Analyst (Scout chat, role-derived scope) ----
//
// A companion surface for interrogating participant/org data. Scope is
// resolved SERVER-SIDE from the caller's role, not chosen by the client:
// platform staff default to a cross-tenant "platform" view (every org and
// participant) and can drill into any org/participant; org admins are
// pinned to their own org. `orgId`/`userId` on a send are only an optional
// drill-in hint for a brand-new chat — never a scope the client asserts.

export type AnalystScopeLevel = 'platform' | 'org' | 'user';

export interface AnalystChat {
  id: string;
  /** Fixed at creation. */
  scope: { level: AnalystScopeLevel; orgId?: string; userId?: string };
  /** Human-readable resolved scope, e.g. "All organizations and participants",
   *  an org name, or a participant name. */
  scopeLabel: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  title?: string;
}

/** One snippet cited by a semantic search source. */
export interface AnalystSearchSnippet {
  sourceId: string;
  sourceType: string | null;
  /** Research pseudonym of the participant the passage came from. */
  participant?: string | null;
  ts: string | null;
  score: number;
  textPreview: string;
}

/** A source the analyst consulted for an answer — the citation behind a reply. */
export type AnalystSource =
  | { kind: 'sql'; purpose?: string; sql: string; rowCount: number; rows: Record<string, unknown>[]; error?: string }
  | { kind: 'metric'; metric: string; sinceDays: number; rowCount: number; rows: Record<string, unknown>[] }
  | { kind: 'search'; query: string; count: number; snippets: AnalystSearchSnippet[]; error?: string };

export interface AnalystStoredMessage {
  id: string;
  chatId: string;
  role: 'user' | 'assistant';
  /** Semantic HTML, rendered via [innerHTML] in the transcript. */
  content: string;
  timestamp: string;
  /** Citations behind an assistant turn (the tool calls that produced it). */
  sources?: AnalystSource[];
}

export interface ListAnalystChatsResponse {
  policyNotice: string;
  /** The caller's own chats — platform staff see their platform-wide history,
   *  org admins see theirs (already org-pinned server-side). */
  conversations: AnalystChat[];
  access: 'platform' | 'org';
  /** The caller's default scope label, shown until a chat resolves its own. */
  defaultLabel: string | null;
}

export interface GetAnalystChatResponse {
  conversation: AnalystChat;
  messages: AnalystStoredMessage[];
}

export interface StreamAnalystMessageBody {
  message: string;
  chatId?: string;
  /** Optional drill-in target for a brand-new chat — ignored once a chat exists. */
  orgId?: string;
  userId?: string;
}

/** Same reasons as participant access. One list lives in shared/participant-access. */
export type ResearchAccessReason = ParticipantAccessReason;

export interface ResolveResearchPseudonymBody {
  pseudonym: string;
  reason: ResearchAccessReason;
  note?: string;
}

export interface ResolveResearchPseudonymResponse {
  /** Admin route of the participant page, for example /participants/abc. */
  participantUrlPath: string;
  sessionId: string;
  expiresAt: string;
}

export interface StreamAnalystMessageResult {
  conversationId: string;
  message: AnalystStoredMessage;
}

export type ProvenanceRef = { kind: 'personal' } | { kind: 'organization'; orgId: string; divisionId: string };
export interface UnsortedCandidate { ref: ProvenanceRef; key: string; seatId?: string; name: string; evidence: string[] }
export interface UnsortedRun {
  userId: string; runId: string; programId: string; displayName: string;
  status: 'active' | 'completed' | 'archived'; enrolledAt: string; reason: string;
  candidates: UnsortedCandidate[];
}
export type ResolveRunTarget =
  | { kind: 'organization'; orgId: string; divisionId: string; seatId?: string }
  | { kind: 'personal' };
export interface ResolveRunBody { target: ResolveRunTarget; reason: string; participantConfirmed?: boolean }
export interface DeliverSeatResult { userId: string; created: string[] }

// Inbox (VOFF-284). Mirrors vo-api src/types/inbox.ts.
export type InboxAudience =
  | { mode: 'users'; userIds: string[] }
  | { mode: 'org'; orgId: string }
  | { mode: 'division'; orgId: string; divisionId: string }
  | { mode: 'everyone' };

export type InboxMessageStatus = 'draft' | 'sending' | 'sent' | 'withdrawn';

export interface InboxMessage {
  id: string;
  kind: 'alert' | 'notification' | 'survey' | 'promotion';
  sender: { type: string; name: string };
  title: string;
  preview: string;
  body: string;
  cta?: { label: string; url: string };
  audience: InboxAudience;
  emailMirror?: boolean;
  status: InboxMessageStatus;
  createdAt: string;
  updatedAt: string;
  sentAt?: string;
  recipientCount?: number;
  emailedCount?: number;
  emailFailedCount?: number;
  withdrawnAt?: string;
}

export interface InboxDraftBody {
  kind: 'alert';
  title: string;
  preview: string;
  body: string;
  cta: { label: string; url: string } | null;
  audience: InboxAudience;
  emailMirror: boolean;
}

/**
 * Where an inbox call goes. Null is the staff Outbox (`/admin/inbox/messages`).
 * An org or division is that scope's alerts (`/admin/organizations/:id[/divisions/:id]/alerts`, VOFF-502).
 */
export type InboxScope = { orgId: string; divisionId?: string | null } | null;

export interface InboxStats { recipients: number; read: number; acted: number; dismissed: number; emailed: number }

export interface InboxAudiencePreview {
  count: number;
  /** How many of them would also get the email copy. */
  emailCount: number;
  sample: { userId: string; name: string; email: string }[];
}

@Injectable({ providedIn: 'root' })
export class AdminApiService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private base = `${environment.apiUrl}/admin`;
  private apiBase = `${environment.apiUrl}`;

  // ── Provenance: runs that need sorting (staff.exceptional.issue) ──────────
  getUnsortedRuns(userId?: string): Observable<{ runs: UnsortedRun[] }> {
    const params = userId ? new HttpParams().set('userId', userId) : undefined;
    return this.http.get<{ runs: UnsortedRun[] }>(`${this.base}/provenance/unsorted`, { params });
  }

  resolveUnsortedRun(userId: string, runId: string, body: ResolveRunBody): Observable<{ run: unknown }> {
    return this.http.post<{ run: unknown }>(
      `${this.base}/provenance/runs/${encodeURIComponent(userId)}/${encodeURIComponent(runId)}/resolve`, body);
  }

  deliverSeatRuns(orgId: string, divisionId: string, seatId: string, reason: string): Observable<DeliverSeatResult> {
    return this.http.post<DeliverSeatResult>(
      `${this.base}/provenance/seats/${encodeURIComponent(orgId)}/${encodeURIComponent(divisionId)}/${encodeURIComponent(seatId)}/deliver`, { reason });
  }

  // ── Branding (white-label T28) ────────────────────────────────────────────
  getBranding(orgId: string, divisionId: string | null): Observable<BrandingResponse> {
    return this.http.get<BrandingResponse>(`${this.scoutBase(orgId, divisionId)}/branding`);
  }

  saveBranding(orgId: string, divisionId: string | null, body: BrandingUpdate, revision: number): Observable<{ branding: BrandingDoc; effective: EffectiveBranding }> {
    return this.http.put<{ branding: BrandingDoc; effective: EffectiveBranding }>(`${this.scoutBase(orgId, divisionId)}/branding`, body,
      { headers: new HttpHeaders({ 'If-Match': String(revision) }) });
  }

  uploadBrandingLogo(orgId: string, divisionId: string | null, file: File): Observable<{ path: string; contentType: string; url: string }> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<{ path: string; contentType: string; url: string }>(`${this.scoutBase(orgId, divisionId)}/branding/logo`, form);
  }

  // ── Messages (white-label T29) ────────────────────────────────────────────
  getCommunications(orgId: string, divisionId: string | null): Observable<CommunicationsResponse> {
    return this.http.get<CommunicationsResponse>(`${this.scoutBase(orgId, divisionId)}/communications`);
  }

  saveCommunications(orgId: string, divisionId: string | null, body: CommunicationsUpdate, revision: number): Observable<{ communications: CommunicationsDoc; effective: EffectiveCommunications }> {
    return this.http.put<{ communications: CommunicationsDoc; effective: EffectiveCommunications }>(`${this.scoutBase(orgId, divisionId)}/communications`, body,
      { headers: new HttpHeaders({ 'If-Match': String(revision) }) });
  }

  previewCommunication(orgId: string, divisionId: string | null, body: CommunicationPreviewRequest): Observable<CommunicationPreview> {
    return this.http.post<CommunicationPreview>(`${this.scoutBase(orgId, divisionId)}/communications/preview`, body);
  }

  // ── Scout Settings (white-label T06–T10) ──────────────────────────────────
  // One base for the organization or one of its divisions.
  // ── Setup checklist and effective settings (white-label T30) ──────────────
  getSetupChecklist(orgId: string): Observable<SetupChecklist> {
    return this.http.get<SetupChecklist>(`${this.base}/organizations/${orgId}/setup-checklist`);
  }

  getEffectiveSettings(orgId: string, divisionId: string): Observable<EffectiveSettings> {
    return this.http.get<EffectiveSettings>(`${this.base}/organizations/${orgId}/divisions/${divisionId}/effective-settings`);
  }

  private scoutBase(orgId: string, divisionId: string | null): string {
    return `${this.base}/organizations/${orgId}${divisionId ? `/divisions/${divisionId}` : ''}`;
  }

  getScoutPolicy(orgId: string, divisionId: string | null): Observable<ScoutPolicyResponse> {
    return this.http.get<ScoutPolicyResponse>(`${this.scoutBase(orgId, divisionId)}/scout-policy`);
  }

  saveScoutPolicyDraft(orgId: string, divisionId: string | null, draft: OrgScoutPolicyContent | DivisionScoutPolicyContent, rev: number): Observable<ScoutPolicyState<unknown>> {
    return this.http.put<ScoutPolicyState<unknown>>(`${this.scoutBase(orgId, divisionId)}/scout-policy/draft`, draft,
      { headers: new HttpHeaders({ 'If-Match': String(rev) }) });
  }

  getScoutPolicyVersion(orgId: string, divisionId: string | null, version: number): Observable<ScoutPolicyVersionMeta & { content: OrgScoutPolicyContent & DivisionScoutPolicyContent }> {
    return this.http.get<ScoutPolicyVersionMeta & { content: OrgScoutPolicyContent & DivisionScoutPolicyContent }>(`${this.scoutBase(orgId, divisionId)}/scout-policy/versions/${version}`);
  }

  publishScoutPolicy(orgId: string, divisionId: string | null, notes: string): Observable<ScoutPolicyVersionMeta> {
    return this.http.post<ScoutPolicyVersionMeta>(`${this.scoutBase(orgId, divisionId)}/scout-policy/publish`, { notes });
  }

  getScoutPolicyEffective(orgId: string, divisionId: string | null, drafts: boolean): Observable<{ effective: EffectiveScoutPolicy; rendered: string }> {
    const draft = drafts ? (divisionId ? 'organization,division' : 'organization') : '';
    return this.http.get<{ effective: EffectiveScoutPolicy; rendered: string }>(`${this.scoutBase(orgId, divisionId)}/scout-policy/effective`,
      { params: draft ? new HttpParams().set('draft', draft) : undefined });
  }

  previewScout(orgId: string, divisionId: string | null, question: string, useDrafts: boolean): Observable<ScoutPreviewResult> {
    return this.http.post<ScoutPreviewResult>(`${this.scoutBase(orgId, divisionId)}/scout-policy/preview`, { question, useDrafts });
  }

  listOrgResources(orgId: string, divisionId: string | null): Observable<{ resources: OrgResource[] }> {
    return this.http.get<{ resources: OrgResource[] }>(`${this.scoutBase(orgId, divisionId)}/resources`);
  }

  getOrgResource(orgId: string, divisionId: string | null, id: string): Observable<OrgResource & { versions: OrgResourceVersion[] }> {
    return this.http.get<OrgResource & { versions: OrgResourceVersion[] }>(`${this.scoutBase(orgId, divisionId)}/resources/${id}`);
  }

  /** Documents go as multipart; pasted text and links as JSON. */
  createOrgResource(orgId: string, divisionId: string | null, body: FormData | Record<string, unknown>): Observable<OrgResource> {
    return this.http.post<OrgResource>(`${this.scoutBase(orgId, divisionId)}/resources`, body);
  }

  updateOrgResource(orgId: string, divisionId: string | null, id: string, patch: Record<string, unknown>): Observable<OrgResource> {
    return this.http.patch<OrgResource>(`${this.scoutBase(orgId, divisionId)}/resources/${id}`, patch);
  }

  replaceOrgResource(orgId: string, divisionId: string | null, id: string, body: FormData | { text: string }): Observable<OrgResource> {
    return this.http.post<OrgResource>(`${this.scoutBase(orgId, divisionId)}/resources/${id}/versions`, body);
  }

  withdrawOrgResource(orgId: string, divisionId: string | null, id: string): Observable<OrgResource> {
    return this.http.post<OrgResource>(`${this.scoutBase(orgId, divisionId)}/resources/${id}/withdraw`, {});
  }

  reindexOrgResource(orgId: string, divisionId: string | null, id: string): Observable<OrgResource & { versions: OrgResourceVersion[] }> {
    return this.http.post<OrgResource & { versions: OrgResourceVersion[] }>(`${this.scoutBase(orgId, divisionId)}/resources/${id}/reindex`, {});
  }

  orgResourceDownload(orgId: string, divisionId: string | null, id: string): Observable<{ url: string; expiresAt: string }> {
    return this.http.get<{ url: string; expiresAt: string }>(`${this.scoutBase(orgId, divisionId)}/resources/${id}/download`);
  }

  listOrgContacts(orgId: string, divisionId: string | null): Observable<{ contacts: OrgContact[] }> {
    return this.http.get<{ contacts: OrgContact[] }>(`${this.scoutBase(orgId, divisionId)}/contacts`);
  }

  createOrgContact(orgId: string, divisionId: string | null, body: Partial<OrgContact>): Observable<OrgContact> {
    return this.http.post<OrgContact>(`${this.scoutBase(orgId, divisionId)}/contacts`, body);
  }

  updateOrgContact(orgId: string, divisionId: string | null, id: string, patch: Partial<OrgContact>): Observable<OrgContact> {
    return this.http.patch<OrgContact>(`${this.scoutBase(orgId, divisionId)}/contacts/${id}`, patch);
  }

  archiveOrgContact(orgId: string, divisionId: string | null, id: string): Observable<OrgContact> {
    return this.http.post<OrgContact>(`${this.scoutBase(orgId, divisionId)}/contacts/${id}/archive`, {});
  }

  /** Self-service org creation. Authenticated; caller is the new owner. */
  registerOrganization(body: RegisterOrganizationRequest): Observable<RegisterOrganizationResponse> {
    return this.http.post<RegisterOrganizationResponse>(`${this.apiBase}/register/organization`, body);
  }

  /** Triggers the Firebase email-verification link for the current user. */
  sendVerificationEmail(): Observable<void> {
    return this.http.post<void>(`${this.apiBase}/me/send-verification-email`, { surface: 'admin' });
  }

  listPrograms(): Observable<ProgramListItem[]> {
    return this.http.get<ProgramListItem[]>(`${this.apiBase}/programs`);
  }

  getProgram(programId: string): Observable<Program> {
    return this.http
      .get<Program>(`${this.apiBase}/programs/${programId}`)
      .pipe(map(migrateLegacyArtifactSources));
  }

  /** Curriculum staff: every program, including archived and unpublished ones.
   * The public /programs routes serve only published VentureOff programs. */
  listAllPrograms(): Observable<ProgramListItem[]> {
    return this.http.get<ProgramListItem[]>(`${this.base}/programs`);
  }

  getOrgLibrary(orgId: string): Observable<{ rows: LibraryRow[] }> {
    return this.http.get<{ rows: LibraryRow[] }>(`${this.base}/libraries/${orgId}`);
  }

  getDivisionLibrary(orgId: string, divisionId: string): Observable<{ rows: LibraryRow[]; effective: EffectiveLibraryEntry[] }> {
    return this.http.get<{ rows: LibraryRow[]; effective: EffectiveLibraryEntry[] }>(`${this.base}/libraries/${orgId}/divisions/${divisionId}`);
  }

  updateOrgLibraryProgram(orgId: string, programId: string, body: LibraryOrgUpdate): Observable<{ row: LibraryRow }> {
    return this.http.put<{ row: LibraryRow }>(`${this.base}/libraries/${orgId}/programs/${encodeURIComponent(programId)}`, body);
  }

  updateDivisionLibraryProgram(orgId: string, divisionId: string, programId: string, body: LibraryDivisionUpdate): Observable<{ row: LibraryRow }> {
    return this.http.put<{ row: LibraryRow }>(`${this.base}/libraries/${orgId}/divisions/${divisionId}/programs/${encodeURIComponent(programId)}`, body);
  }

  duplicateLibraryProgram(orgId: string, body: { programId: string; divisionId?: string; title?: string }): Observable<LibraryDuplicateResult> {
    return this.http.post<LibraryDuplicateResult>(`${this.base}/libraries/${orgId}/duplicate`, body);
  }

  /** Curriculum staff: what each program requires before a participant can start it. */
  getProgramFlow(): Observable<ProgramFlow> {
    return this.http.get<ProgramFlow>(`${this.base}/programs/flow`);
  }

  /** Curriculum staff: any program by id, whatever its status or owner. */
  getProgramForEditing(programId: string): Observable<Program> {
    return this.http
      .get<Program>(`${this.base}/programs/${programId}`)
      .pipe(map(migrateLegacyArtifactSources));
  }

  createProgram(body: CreateProgramRequest): Observable<Program> {
    return this.http.post<Program>(`${this.base}/programs`, body);
  }

  /** Create an organization-owned program (curriculum authors and staff, white-label T23). */
  createOwnedProgram(body: { title: string; description: string; owner: { orgId: string; divisionId?: string } }): Observable<{ id: string; title: string }> {
    return this.http.post<{ id: string; title: string }>(`${this.base}/programs`, body);
  }

  updateProgram(programId: string, body: UpdateProgramRequest): Observable<Program> {
    return this.http.put<Program>(`${this.base}/programs/${programId}`, body);
  }

  updateProgramStatus(programId: string, status: ProgramStatus): Observable<Program> {
    return this.http.patch<Program>(`${this.base}/programs/${programId}/status`, { status });
  }

  saveProgramStructure(programId: string, modules: ProgramModule[]): Observable<Program> {
    return this.http.put<Program>(`${this.base}/programs/${programId}/structure`, { modules });
  }

  // --- Program versioning & branching ---
  //
  // The editor edits a single open BRANCH (not the live program doc). Branch
  // content carries the same legacy artifact shapes the program doc might, so we
  // run it through the same migration on load.

  private static migrateBranch(branch: ProgramBranch): ProgramBranch {
    const migrated = migrateLegacyArtifactSources({ modules: branch.content } as Program);
    return { ...branch, content: migrated.modules };
  }

  listProgramVersions(programId: string): Observable<ProgramVersionMeta[]> {
    return this.http.get<ProgramVersionMeta[]>(`${this.base}/programs/${programId}/versions`);
  }

  getProgramVersion(programId: string, versionNumber: number): Observable<ProgramVersion> {
    return this.http.get<ProgramVersion>(`${this.base}/programs/${programId}/versions/${versionNumber}`);
  }

  /** Set defaultVersion = n (and rollback). Returns the updated program. */
  promoteVersion(programId: string, versionNumber: number): Observable<Program> {
    return this.http.post<Program>(`${this.base}/programs/${programId}/versions/${versionNumber}/promote`, {});
  }

  archiveVersion(programId: string, versionNumber: number): Observable<ProgramVersionMeta> {
    return this.http.post<ProgramVersionMeta>(`${this.base}/programs/${programId}/versions/${versionNumber}/archive`, {});
  }

  listBranches(programId: string): Observable<ProgramBranchMeta[]> {
    return this.http.get<ProgramBranchMeta[]>(`${this.base}/programs/${programId}/branches`);
  }

  createBranch(programId: string, body: { name: string; baseVersion?: number }): Observable<ProgramBranch> {
    return this.http
      .post<ProgramBranch>(`${this.base}/programs/${programId}/branches`, body)
      .pipe(map(AdminApiService.migrateBranch));
  }

  getBranch(programId: string, branchId: string): Observable<ProgramBranch> {
    return this.http
      .get<ProgramBranch>(`${this.base}/programs/${programId}/branches/${branchId}`)
      .pipe(map(AdminApiService.migrateBranch));
  }

  /** Autosave branch content. `rev` rides the If-Match header; a stale write 409s. */
  saveBranchStructure(
    programId: string,
    branchId: string,
    modules: ProgramModule[],
    rev: number,
    requirements?: ProgramRequirement[] | null,
  ): Observable<ProgramBranch> {
    const headers = new HttpHeaders({ 'If-Match': String(rev) });
    return this.http.put<ProgramBranch>(
      `${this.base}/programs/${programId}/branches/${branchId}/structure`,
      requirements ? { modules, requirements } : { modules },
      { headers },
    );
  }

  submitBranch(programId: string, branchId: string): Observable<ProgramBranchMeta> {
    return this.http.post<ProgramBranchMeta>(`${this.base}/programs/${programId}/branches/${branchId}/submit`, {});
  }

  reopenBranch(programId: string, branchId: string): Observable<ProgramBranchMeta> {
    return this.http.post<ProgramBranchMeta>(`${this.base}/programs/${programId}/branches/${branchId}/reopen`, {});
  }

  abandonBranch(programId: string, branchId: string): Observable<ProgramBranchMeta> {
    return this.http.post<ProgramBranchMeta>(`${this.base}/programs/${programId}/branches/${branchId}/abandon`, {});
  }

  /** Fast-forward publish the branch to a new version. `promote` sets it as default. */
  /**
   * How many participants publishing this branch would lock out. Read-only, and
   * asked BEFORE publishing so the number appears in the confirmation rather
   * than in support tickets afterwards.
   */
  lockoutForecast(
    programId: string,
    branchId: string,
  ): Observable<{ total: number; newlyBlocked: number; alreadyBlocked: number; applicable: boolean }> {
    return this.http.get<{
      total: number;
      newlyBlocked: number;
      alreadyBlocked: number;
      applicable: boolean;
    }>(`${this.base}/programs/${programId}/branches/${branchId}/lockout-forecast`);
  }

  publishBranch(programId: string, branchId: string, promote: boolean): Observable<PublishBranchResult> {
    return this.http.post<PublishBranchResult>(
      `${this.base}/programs/${programId}/branches/${branchId}/publish`,
      { promote },
    );
  }

  /** Run the 3-way merge of main into the branch. Auto-merges, or returns conflicts. */
  updateBranchFromMain(programId: string, branchId: string): Observable<UpdateFromMainResult> {
    return this.http
      .post<UpdateFromMainResult>(`${this.base}/programs/${programId}/branches/${branchId}/update-from-main`, {})
      .pipe(
        map((r) => (r.status === 'merged' ? { ...r, branch: AdminApiService.migrateBranch(r.branch) } : r)),
      );
  }

  /** Submit conflict resolutions for a pending merge. Returns the merged branch. */
  resolveBranchMerge(
    programId: string,
    branchId: string,
    resolutions: MergeResolutionInput[],
  ): Observable<ProgramBranch> {
    return this.http
      .post<ProgramBranch>(`${this.base}/programs/${programId}/branches/${branchId}/resolve`, { resolutions })
      .pipe(map(AdminApiService.migrateBranch));
  }

  /** Discard a pending merge (content/baseVersion untouched). */
  abortBranchMerge(programId: string, branchId: string): Observable<ProgramBranch> {
    return this.http
      .post<ProgramBranch>(`${this.base}/programs/${programId}/branches/${branchId}/abort-merge`, {})
      .pipe(map(AdminApiService.migrateBranch));
  }

  /**
   * Upload a file that becomes the download source for a File block (download
   * mode). Returns a stable tokenized URL the editor saves onto the block. The
   * auth interceptor attaches the bearer token; the browser sets the multipart
   * Content-Type boundary, so we must not set it ourselves.
   */
  uploadProgramAsset(programId: string, file: File): Observable<ProgramAssetUploadResult> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<ProgramAssetUploadResult>(
      `${this.base}/programs/${programId}/assets`,
      form,
    );
  }

  deleteProgram(programId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/programs/${programId}`);
  }

  listUsers(params: ListUsersParams = {}): Observable<ListUsersResponse> {
    let httpParams = new HttpParams();
    if (params.limit) httpParams = httpParams.set('limit', String(params.limit));
    if (params.cursor) httpParams = httpParams.set('cursor', params.cursor);
    if (params.search) httpParams = httpParams.set('search', params.search);
    if (params.enrolledOnly !== undefined) httpParams = httpParams.set('enrolledOnly', String(params.enrolledOnly));
    return this.http.get<ListUsersResponse>(`${this.base}/users`, { params: httpParams });
  }

  /** Every account, no progress rollup. Fast enough to browse. */
  listUserDirectory(params: Omit<ListUsersParams, 'enrolledOnly'> = {}): Observable<UserDirectoryResponse> {
    let httpParams = new HttpParams().set('view', 'directory');
    if (params.limit) httpParams = httpParams.set('limit', String(params.limit));
    if (params.cursor) httpParams = httpParams.set('cursor', params.cursor);
    if (params.search) httpParams = httpParams.set('search', params.search);
    return this.http.get<UserDirectoryResponse>(`${this.base}/users`, { params: httpParams });
  }

  getUser(userId: string): Observable<User> {
    return this.http.get<User>(`${this.base}/users/${userId}`);
  }

  /**
   * A participant's runs, with completion.
   *
   * Someone can hold several runs of the same program, one per business idea.
   * Every other read on this page shows ONE of them, so without this an admin
   * had no way to know which — or that there were others.
   */
  getOrgParticipantRuns(orgId: string, userId: string): Observable<AdminRunsResponse> {
    return this.http.get<AdminRunsResponse>(`${this.base}/organizations/${orgId}/participants/${userId}/runs`);
  }

  getUserRuns(userId: string): Observable<AdminRunsResponse> {
    return this.http.get<AdminRunsResponse>(`${this.base}/users/${userId}/runs`);
  }

  getProgress(userId: string, programId?: string, runId?: string): Observable<ProgressResponse> {
    let httpParams = new HttpParams();
    if (runId) httpParams = httpParams.set('runId', runId);
    else if (programId) httpParams = httpParams.set('programId', programId);
    return this.http.get<ProgressResponse>(`${this.base}/users/${userId}/progress`, {
      params: httpParams,
    });
  }

  getConversations(userId: string, runId?: string): Observable<ConversationsResponse> {
    let httpParams = new HttpParams();
    if (runId) httpParams = httpParams.set('runId', runId);
    return this.http.get<ConversationsResponse>(`${this.base}/users/${userId}/conversations`, {
      params: httpParams,
    });
  }

  /** Foundation, dashboard Scout and practice interview chats. Needs open participant access. */
  getOtherChats(userId: string): Observable<OtherChatsResponse> {
    return this.http.get<OtherChatsResponse>(`${this.base}/users/${userId}/other-chats`);
  }

  getMemories(userId: string): Observable<MemoriesResponse> {
    return this.http.get<MemoriesResponse>(`${this.base}/users/${userId}/memories`);
  }

  getDecisions(userId: string): Observable<DecisionsResponse> {
    return this.http.get<DecisionsResponse>(`${this.base}/users/${userId}/decisions`);
  }

  /** Participant artifacts through the audited staff support route. */
  getArtifacts(userId: string, runId?: string): Observable<ArtifactsResponse> {
    let httpParams = new HttpParams();
    if (runId) httpParams = httpParams.set('runId', runId);
    return this.http.get<ArtifactsResponse>(`${this.base}/users/${userId}/artifacts`, {
      params: httpParams,
    });
  }

  /** Every retained version of one artifact, oldest first. Powers the viewer's version navigator. */
  getArtifactVersions(userId: string, blockId: string): Observable<ArtifactVersionsResponse> {
    return this.http.get<ArtifactVersionsResponse>(
      `${this.base}/users/${userId}/artifacts/${blockId}/versions`,
    );
  }

  /**
   * Generate an AI dossier for a participant — a candid interpretive read for
   * the admin. A separate Claude call from Scout, assembled fresh each time and
   * not persisted. Can take several seconds to return.
   */
  generateDossier(userId: string, programId?: string): Observable<DossierResponse> {
    return this.http.post<DossierResponse>(
      `${this.base}/users/${userId}/dossier`,
      programId ? { programId } : {},
    );
  }

  /** The latest saved dossier for a participant, or null if none generated yet. */
  getLatestDossier(userId: string): Observable<LatestDossierResponse> {
    return this.http.get<LatestDossierResponse>(`${this.base}/users/${userId}/dossier`);
  }

  /** Full dossier history, oldest first. Powers the dossier version navigator. */
  listDossiers(userId: string): Observable<DossierListResponse> {
    return this.http.get<DossierListResponse>(`${this.base}/users/${userId}/dossiers`);
  }

  resetUser(userId: string, body: ResetRequest): Observable<ResetResponse> {
    return this.http.post<ResetResponse>(`${this.base}/users/${userId}/reset`, body);
  }

  // ---- Roles (super-admin only) ----

  getUserRoles(userId: string): Observable<UserRolesResponse> {
    return this.http.get<UserRolesResponse>(`${this.base}/users/${userId}/roles`);
  }

  grantPlatformRole(userId: string, role: PlatformRoleName): Observable<{ role: UserRoleAssignment }> {
    return this.http.post<{ role: UserRoleAssignment }>(`${this.base}/users/${userId}/roles`, { role });
  }

  revokeUserRole(userId: string, roleId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/users/${userId}/roles/${roleId}`);
  }

  // ---- Inline permission grants (super-admin only) ----

  getPermissionCatalog(): Observable<PermissionCatalog> {
    return this.http.get<PermissionCatalog>(`${this.base}/permissions/catalog`);
  }

  // ---- Team & access (VOFF-350/357) ----

  listCurriculumAuthors(orgId: string): Observable<{ grants: CurriculumAuthorGrant[] }> {
    return this.http.get<{ grants: CurriculumAuthorGrant[] }>(`${this.base}/organizations/${orgId}/curriculum-authors`);
  }

  grantCurriculumAuthor(orgId: string, body: { userId: string; divisionId: string | null; reason: string }): Observable<{ grant: CurriculumAuthorGrant }> {
    return this.http.post<{ grant: CurriculumAuthorGrant }>(`${this.base}/organizations/${orgId}/curriculum-authors`, body);
  }

  revokeCurriculumAuthor(orgId: string, grantId: string, body: { reason: string }): Observable<{ grant: CurriculumAuthorGrant }> {
    return this.http.post<{ grant: CurriculumAuthorGrant }>(`${this.base}/organizations/${orgId}/curriculum-authors/${encodeURIComponent(grantId)}/revoke`, body);
  }

  getTeamAccess(orgId: string): Observable<TeamAccessResponse> {
    return this.http.get<TeamAccessResponse>(`${this.base}/organizations/${orgId}/access`);
  }

  inviteTeamMember(orgId: string, body: TeamInviteBody): Observable<{ invitation: Invitation }> {
    return this.http.post<{ invitation: Invitation }>(`${this.base}/organizations/${orgId}/access/invitations`, body);
  }

  revokeTeamInvitation(orgId: string, invitationId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/organizations/${orgId}/access/invitations/${invitationId}`);
  }

  updateTeamAssignment(orgId: string, assignmentId: string, body: { full: boolean; levels: Record<string, string>; version: number }): Observable<unknown> {
    return this.http.patch(`${this.base}/organizations/${orgId}/access/assignments/${encodeURIComponent(assignmentId)}`, body);
  }

  removeTeamAssignment(orgId: string, assignmentId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/organizations/${orgId}/access/assignments/${encodeURIComponent(assignmentId)}`);
  }

  removeAllTeamAccess(orgId: string, userId: string): Observable<{ removed: Record<string, number> }> {
    return this.http.post<{ removed: Record<string, number> }>(`${this.base}/organizations/${orgId}/access/members/${userId}/remove-all`, {});
  }

  getTeamAccessHistory(orgId: string): Observable<{ entries: TeamAccessHistoryEntry[] }> {
    return this.http.get<{ entries: TeamAccessHistoryEntry[] }>(`${this.base}/organizations/${orgId}/access/history`);
  }

  transferOwnership(orgId: string, body: OwnerTransferBody): Observable<{ invitation: Invitation }> {
    return this.http.post<{ invitation: Invitation }>(`${this.base}/organizations/${orgId}/access/owner-transfer`, body);
  }

  acknowledgeOwnerNotice(orgId: string, version: number): Observable<{ acknowledged: number }> {
    return this.http.post<{ acknowledged: number }>(`${this.base}/organizations/${orgId}/access/owner-notice`, { version });
  }

  // ---- Staff assignments (VOFF-355) ----

  listStaffAssignments(): Observable<StaffAssignmentsResponse> {
    return this.http.get<StaffAssignmentsResponse>(`${this.base}/staff-assignments`);
  }

  createStaffAssignment(body: StaffAssignmentBody): Observable<{ assignment: StaffAssignmentView }> {
    return this.http.post<{ assignment: StaffAssignmentView }>(`${this.base}/staff-assignments`, body);
  }

  updateStaffAssignment(id: string, body: { full: boolean; levels: Record<string, string>; extras: string[]; version: number }): Observable<unknown> {
    return this.http.patch(`${this.base}/staff-assignments/${encodeURIComponent(id)}`, body);
  }

  removeStaffAssignment(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/staff-assignments/${encodeURIComponent(id)}`);
  }

  /** University divisions open for a new VentureOff coach assignment (VOFF-403). */
  getCoachingDivisions(): Observable<{ divisions: CoachingDivisionOption[] }> {
    return this.http.get<{ divisions: CoachingDivisionOption[] }>(`${this.base}/staff-assignments/coaching-divisions`);
  }

  // ---- Exceptional staff access (VOFF-348) ----

  getMyStaffAccess(): Observable<MyStaffAccessResponse> {
    return this.http.get<MyStaffAccessResponse>(`${this.base}/staff-access/me`);
  }

  // ---- Participant access sessions ----

  /** The caller's open access session for a participant. 404 when none is open. */
  getParticipantAccess(userId: string): Observable<{ session: ParticipantAccessSession | null }> {
    return this.http.get<{ session: ParticipantAccessSession | null }>(`${this.base}/participants/${userId}/access`);
  }

  /** Open a 12 hour, logged access session for a participant's private data. */
  openParticipantAccess(
    userId: string,
    body: { reason: ParticipantAccessReason; note?: string },
  ): Observable<{ sessionId: string; reason: ParticipantAccessReason; expiresAt: string }> {
    return this.http.post<{ sessionId: string; reason: ParticipantAccessReason; expiresAt: string }>(
      `${this.base}/participants/${userId}/access`,
      body,
    );
  }

  /** The staff access log for an organization, as a CSV download. */
  /** The purge record for this organization, or null when none has run (VOFF-496). */
  getOrgPurge(orgId: string): Observable<{ purge: OrgPurgeSummary | null }> {
    return this.http.get<{ purge: OrgPurgeSummary | null }>(`${this.base}/organizations/${orgId}/purge`);
  }

  /** Read-only manifest of what the purge would delete and keep. */
  planOrgPurge(orgId: string): Observable<OrgPurgePlan> {
    return this.http.get<OrgPurgePlan>(`${this.base}/organizations/${orgId}/purge/plan`);
  }

  /** Deletes the organization's data. Needs two-step sign-in and a Destructive data grant. */
  executeOrgPurge(orgId: string, body: { confirmOrgName: string; reason: string }): Observable<{ purge: OrgPurgeSummary; alreadyCompleted: boolean }> {
    return this.http.post<{ purge: OrgPurgeSummary; alreadyCompleted: boolean }>(`${this.base}/organizations/${orgId}/purge/execute`, body);
  }

  downloadOrgPurgeCertificate(orgId: string, format: 'md' | 'json'): Observable<Blob> {
    return this.http.get(`${this.base}/organizations/${orgId}/purge/certificate`, { params: { format }, responseType: 'blob' });
  }

  setOrgLegalHold(orgId: string, reason: string): Observable<{ legalHold: NonNullable<Organization['legalHold']> }> {
    return this.http.post<{ legalHold: NonNullable<Organization['legalHold']> }>(`${this.base}/organizations/${orgId}/legal-hold`, { reason });
  }

  clearOrgLegalHold(orgId: string): Observable<{ legalHold: null }> {
    return this.http.delete<{ legalHold: null }>(`${this.base}/organizations/${orgId}/legal-hold`);
  }

  downloadParticipantAccessLog(orgId: string, from?: string, to?: string): Observable<Blob> {
    const params: Record<string, string> = { format: 'csv' };
    if (from) params['from'] = from;
    if (to) params['to'] = to;
    return this.http.get(`${this.base}/organizations/${orgId}/participant-access-log`, { params, responseType: 'blob' });
  }

  /** Staff who can receive exceptional access now (same check as issuing). */
  listEligibleStaff(): Observable<{ people: { userId: string; email: string; name: string }[] }> {
    return this.http.get<{ people: { userId: string; email: string; name: string }[] }>(`${this.base}/staff-access/eligible`);
  }

  listStaffAccessGrants(): Observable<StaffAccessGrantsResponse> {
    return this.http.get<StaffAccessGrantsResponse>(`${this.base}/staff-access/grants`);
  }

  issueStaffAccessGrant(body: IssueStaffAccessBody): Observable<{ grant: StaffAccessGrant }> {
    return this.http.post<{ grant: StaffAccessGrant }>(`${this.base}/staff-access/grants`, body);
  }

  revokeStaffAccessGrant(grantId: string): Observable<{ grant: StaffAccessGrant }> {
    return this.http.delete<{ grant: StaffAccessGrant }>(`${this.base}/staff-access/grants/${grantId}`);
  }

  getUserPermissionGrants(userId: string): Observable<PermissionGrantsResponse> {
    return this.http.get<PermissionGrantsResponse>(`${this.base}/users/${userId}/permission-grants`);
  }

  grantUserPermission(userId: string, body: GrantPermissionBody): Observable<{ grant: PermissionGrant }> {
    return this.http.post<{ grant: PermissionGrant }>(
      `${this.base}/users/${userId}/permission-grants`,
      body,
    );
  }

  revokeUserPermission(userId: string, grantId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/users/${userId}/permission-grants/${grantId}`);
  }

  // ---- Run grants (platform-admin only) ----
  // The ledger behind "may this participant open another run". A comp is how
  // support and demos hand someone a run without a purchase.

  getUserRunGrants(userId: string): Observable<{ grants: AdminRunGrant[] }> {
    return this.http.get<{ grants: AdminRunGrant[] }>(`${this.base}/users/${userId}/run-grants`);
  }

  compRunGrant(userId: string, programId: string, reason: string): Observable<{ grant: AdminRunGrant }> {
    return this.http.post<{ grant: AdminRunGrant }>(`${this.base}/users/${userId}/run-grants`, {
      programId,
      ...(reason ? { reason } : {}),
    });
  }

  /** Only an unspent comp can be taken back. */
  revokeRunGrant(userId: string, grantId: string): Observable<{ grantId: string; revoked: boolean }> {
    return this.http.delete<{ grantId: string; revoked: boolean }>(
      `${this.base}/users/${userId}/run-grants/${grantId}`,
    );
  }

  // ---- Curriculum preview (platform-admin only) ----
  // Holds every participant gate open for one account (Program Foundation
  // prerequisite, sequential modules, lock blocks, run entitlement) so a
  // proctor can read the curriculum without answering it. Progress untouched.

  setCurriculumPreview(userId: string, enabled: boolean): Observable<{ userId: string; curriculumPreview: boolean }> {
    return this.http.put<{ userId: string; curriculumPreview: boolean }>(
      `${this.base}/users/${userId}/curriculum-preview`,
      { enabled },
    );
  }

  // ---- Program assignment + archive (super-admin only) ----

  getUserPrograms(userId: string): Observable<UserProgramsResponse> {
    return this.http.get<UserProgramsResponse>(`${this.base}/users/${userId}/programs`);
  }

  assignProgram(userId: string, programId: string): Observable<{ programId: string; roleId: string }> {
    return this.http.post<{ programId: string; roleId: string }>(
      `${this.base}/users/${userId}/programs`,
      { programId },
    );
  }

  archiveProgram(userId: string, programId: string): Observable<{ programId: string; archived: boolean }> {
    return this.http.post<{ programId: string; archived: boolean }>(
      `${this.base}/users/${userId}/programs/${programId}/archive`,
      {},
    );
  }

  unarchiveProgram(userId: string, programId: string): Observable<{ programId: string; archived: boolean }> {
    return this.http.post<{ programId: string; archived: boolean }>(
      `${this.base}/users/${userId}/programs/${programId}/unarchive`,
      {},
    );
  }

  // --- Re-pin (move a participant / cohort to a different program version) ---

  /** Single participant. `dryRun` previews the reconciliation without writing. */
  repinEnrollment(
    programId: string,
    uid: string,
    toVersion: number,
    dryRun: boolean,
  ): Observable<ReconciliationSummary> {
    const qs = dryRun ? '?dryRun=1' : '';
    return this.http.post<ReconciliationSummary>(
      `${this.base}/programs/${programId}/enrollments/${uid}/repin${qs}`,
      { toVersion },
    );
  }

  /** Cohort. `dryRun` previews the aggregate reconciliation without writing. */
  repinCohort(
    programId: string,
    body: { fromVersion?: number; toVersion: number; scope?: CohortScope },
    dryRun: boolean,
  ): Observable<ReconciliationSummary> {
    const qs = dryRun ? '?dryRun=1' : '';
    return this.http.post<ReconciliationSummary>(`${this.base}/programs/${programId}/repin${qs}`, body);
  }

  createOrganization(body: CreateOrganizationRequest): Observable<CreateOrganizationResponse> {
    return this.http.post<CreateOrganizationResponse>(`${this.base}/organizations`, body);
  }

  listOrganizations(): Observable<{ organizations: OrganizationListItem[] }> {
    return this.http.get<{ organizations: OrganizationListItem[] }>(`${this.base}/organizations`);
  }

  getOrganization(orgId: string): Observable<OrganizationDetail> {
    return this.http.get<OrganizationDetail>(`${this.base}/organizations/${orgId}`);
  }

  /**
   * VentureOff coaches assigned to this organization's divisions (VOFF-403).
   * Available to staff with Organizations, Support or Finance access here.
   */
  getOrganizationVentureoffCoaches(orgId: string): Observable<OrganizationVentureoffCoachesResponse> {
    return this.http.get<OrganizationVentureoffCoachesResponse>(`${this.base}/organizations/${orgId}/ventureoff-coaches`);
  }

  activateOrganization(orgId: string, body: ActivateOrgRequest = {}): Observable<{ organization: Organization }> {
    return this.http.post<{ organization: Organization }>(`${this.base}/organizations/${orgId}/activate`, body);
  }

  archiveOrganization(orgId: string, body: ArchiveOrgRequest): Observable<{ organization: Organization }> {
    return this.http.post<{ organization: Organization }>(`${this.base}/organizations/${orgId}/archive`, body);
  }

  unarchiveOrganization(orgId: string): Observable<{ organization: Organization }> {
    return this.http.post<{ organization: Organization }>(`${this.base}/organizations/${orgId}/unarchive`, {});
  }

  /** Org-level settings: billing mode (platform admins) and the seat date rule. */
  updateOrganizationSettings(
    orgId: string,
    body: UpdateOrganizationSettingsRequest,
  ): Observable<{ organization: Organization }> {
    return this.http.patch<{ organization: Organization }>(`${this.base}/organizations/${orgId}/settings`, body);
  }

  listOrgCatalogAssignments(orgId: string): Observable<{ assignments: CatalogAssignment[] }> {
    return this.http.get<{ assignments: CatalogAssignment[] }>(`${this.base}/organizations/${orgId}/catalog-assignments`);
  }

  prefillInvoice(orgId: string, divisionId: string): Observable<{ lines: InvoiceLine[]; pilotSeats: number; division: { id: string; name: string } }> {
    return this.http.get<{ lines: InvoiceLine[]; pilotSeats: number; division: { id: string; name: string } }>(
      `${this.base}/organizations/${orgId}/invoices/prefill`,
      { params: new HttpParams().set('divisionId', divisionId) },
    );
  }

  createInvoiceDraft(orgId: string, body: InvoiceDraftInput): Observable<{ invoice: Invoice }> {
    return this.http.post<{ invoice: Invoice }>(`${this.base}/organizations/${orgId}/invoices`, body);
  }

  updateInvoiceDraft(orgId: string, invoiceId: string, body: Partial<InvoiceDraftInput>): Observable<{ invoice: Invoice }> {
    return this.http.patch<{ invoice: Invoice }>(`${this.base}/organizations/${orgId}/invoices/${invoiceId}`, body);
  }

  sendInvoice(orgId: string, invoiceId: string): Observable<{ invoice: Invoice }> {
    return this.http.post<{ invoice: Invoice }>(`${this.base}/organizations/${orgId}/invoices/${invoiceId}/send`, {});
  }

  resendInvoice(orgId: string, invoiceId: string): Observable<{ invoice: Invoice }> {
    return this.http.post<{ invoice: Invoice }>(`${this.base}/organizations/${orgId}/invoices/${invoiceId}/resend`, {});
  }

  voidInvoice(orgId: string, invoiceId: string): Observable<{ invoice: Invoice }> {
    return this.http.post<{ invoice: Invoice }>(`${this.base}/organizations/${orgId}/invoices/${invoiceId}/void`, {});
  }

  /** Platform admin: mark a division a free pilot, or clear it. */
  /** Platform admin: flip who runs the org (VOFF-297). */
  setOrganizationManagement(orgId: string, managedBy: 'ventureoff' | 'self'): Observable<{ organization: Organization }> {
    return this.http.patch<{ organization: Organization }>(`${this.base}/organizations/${orgId}/management`, { managedBy });
  }

  /** Full platform administrators only. Sets the participant-visibility ceiling and whether coaching is offered. */
  setOrganizationKind(orgId: string, kind: OrganizationKind): Observable<{ organization: Organization }> {
    return this.http.patch<{ organization: Organization }>(`${this.base}/organizations/${orgId}/kind`, { kind });
  }

  setDivisionPilot(orgId: string, divisionId: string, enabled: boolean): Observable<{ division: Division }> {
    return this.http.patch<{ division: Division }>(`${this.rosterBase(orgId, divisionId)}/pilot`, { enabled });
  }

  markInvoicePaid(orgId: string, invoiceId: string, note?: string): Observable<{ invoice: Invoice }> {
    return this.http.post<{ invoice: Invoice }>(
      `${this.base}/organizations/${orgId}/invoices/${invoiceId}/mark-paid`,
      note ? { note } : {},
    );
  }

  /** Staff record an MSA signed outside the app (paper, DocuSign). */
  markMsaAccepted(orgId: string, body: { signerName?: string; signerTitle?: string; reference?: string } = {}): Observable<{ organization: Organization }> {
    return this.http.post<{ organization: Organization }>(`${this.base}/organizations/${orgId}/msa-accepted`, body);
  }

  // ---- MSA (master services agreement) ----

  getOrgMsa(orgId: string): Observable<OrgMsaStatus> {
    return this.http.get<OrgMsaStatus>(`${this.base}/organizations/${orgId}/msa`);
  }

  /** The PDF as a blob; the caller makes an object URL for viewing. */
  getOrgMsaFile(orgId: string): Observable<Blob> {
    return this.http.get(`${this.base}/organizations/${orgId}/msa/file`, { responseType: 'blob' });
  }

  signOrgMsa(orgId: string, body: { name: string; title: string; authorized: true; version: string; sha256: string }): Observable<{ signature: MsaSignatureSummary }> {
    return this.http.post<{ signature: MsaSignatureSummary }>(`${this.base}/organizations/${orgId}/msa/sign`, body);
  }

  uploadOrgMsa(orgId: string, file: File, version: string): Observable<{ document: EffectiveMsa }> {
    const form = new FormData();
    form.append('file', file);
    form.append('version', version);
    return this.http.post<{ document: EffectiveMsa }>(`${this.base}/organizations/${orgId}/msa/document`, form);
  }

  removeOrgMsa(orgId: string): Observable<OrgMsaStatus> {
    return this.http.delete<OrgMsaStatus>(`${this.base}/organizations/${orgId}/msa/document`);
  }

  pendingMsa(): Observable<{ pending: Array<{ orgId: string; orgName: string }> }> {
    return this.http.get<{ pending: Array<{ orgId: string; orgName: string }> }>(`${this.base}/msa/pending`);
  }

  getStandardMsa(): Observable<{ document: EffectiveMsa | null; uploadedAt: string | null }> {
    return this.http.get<{ document: EffectiveMsa | null; uploadedAt: string | null }>(`${this.base}/msa/standard`);
  }

  getStandardMsaFile(): Observable<Blob> {
    return this.http.get(`${this.base}/msa/standard/file`, { responseType: 'blob' });
  }

  uploadStandardMsa(file: File, version: string): Observable<{ document: EffectiveMsa }> {
    const form = new FormData();
    form.append('file', file);
    form.append('version', version);
    return this.http.post<{ document: EffectiveMsa }>(`${this.base}/msa/standard`, form);
  }

  /** The MSA an invited Owner will sign, before they have an account. */
  getInvitationMsa(token: string): Observable<{ document: EffectiveMsa; signed: boolean }> {
    return this.http.get<{ document: EffectiveMsa; signed: boolean }>(`${this.apiBase}/invitations/${encodeURIComponent(token)}/msa`);
  }

  invitationMsaFileUrl(token: string): string {
    return `${this.apiBase}/invitations/${encodeURIComponent(token)}/msa/file`;
  }


  /**
   * Stripe-driven division creation. Creates the division (in pending state),
   * ensures the org's billing account exists, and returns the PaymentIntent
   * client secret for the Payment Element to confirm. The webhook handler
   * flips the division to active on payment_intent.succeeded.
   */
  checkoutDivision(
    orgId: string,
    body: CreateDivisionRequest,
  ): Observable<{
    division: Division;
    clientSecret: string;
    paymentId: string;
    billingAccountId: string;
  }> {
    return this.http.post<{
      division: Division;
      clientSecret: string;
      paymentId: string;
      billingAccountId: string;
    }>(`${this.base}/organizations/${orgId}/divisions/checkout`, body);
  }

  createDivision(orgId: string, body: CreateDivisionRequest): Observable<{ division: Division }> {
    return this.http.post<{ division: Division }>(
      `${this.base}/organizations/${orgId}/divisions`,
      body,
    );
  }

  /** Rename a division. VentureOff staff with Organizations manage only. */
  renameDivision(orgId: string, divisionId: string, name: string): Observable<{ division: Division }> {
    return this.http.patch<{ division: Division }>(`${this.base}/organizations/${orgId}/divisions/${divisionId}`, { name });
  }

  markDivisionSetupFeePaid(
    orgId: string,
    divisionId: string,
    body: MarkSetupFeePaidRequest = {},
  ): Observable<MarkSetupFeePaidResponse> {
    return this.http.post<MarkSetupFeePaidResponse>(
      `${this.base}/organizations/${orgId}/divisions/${divisionId}/setup-fee-paid`,
      body,
    );
  }

  inviteMember(orgId: string, body: InviteMemberRequest): Observable<InviteMemberResponse> {
    return this.http.post<InviteMemberResponse>(
      `${this.base}/organizations/${orgId}/invitations`,
      body,
    );
  }

  // Per-member org-scoped inline permission grants (org owner can manage these).
  listMemberPermissionGrants(orgId: string, userId: string): Observable<PermissionGrantsResponse> {
    return this.http.get<PermissionGrantsResponse>(
      `${this.base}/organizations/${orgId}/members/${userId}/permission-grants`,
    );
  }

  grantMemberPermission(
    orgId: string,
    userId: string,
    permission: string,
    divisionId?: string,
  ): Observable<{ grant: PermissionGrant }> {
    return this.http.post<{ grant: PermissionGrant }>(
      `${this.base}/organizations/${orgId}/members/${userId}/permission-grants`,
      { permission, ...(divisionId ? { divisionId } : {}) },
    );
  }

  revokeMemberPermission(orgId: string, userId: string, grantId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/organizations/${orgId}/members/${userId}/permission-grants/${grantId}`,
    );
  }

  /** Edit a member: change role (within the same tier) and/or display name. (VOFF-87) */
  updateMember(
    orgId: string,
    membershipId: string,
    body: { role?: MembershipRole; name?: string },
  ): Observable<{ membership: MembershipWithUser }> {
    return this.http.patch<{ membership: MembershipWithUser }>(
      `${this.base}/organizations/${orgId}/members/${membershipId}`,
      body,
    );
  }

  /** Remove a member, revoking their access. (VOFF-87) */
  removeMember(orgId: string, membershipId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/organizations/${orgId}/members/${membershipId}`);
  }

  listParticipants(orgId: string): Observable<{ participants: OrgParticipant[] }> {
    return this.http.get<{ participants: OrgParticipant[] }>(`${this.base}/organizations/${orgId}/participants`);
  }

  getParticipantArtifacts(orgId: string, userId: string): Observable<{ artifacts: StoredArtifact[] }> {
    return this.http.get<{ artifacts: StoredArtifact[] }>(`${this.base}/organizations/${orgId}/participants/${userId}/artifacts`);
  }

  getParticipantArtifactVersions(orgId: string, userId: string, blockId: string): Observable<{ versions: StoredArtifact[] }> {
    return this.http.get<{ versions: StoredArtifact[] }>(`${this.base}/organizations/${orgId}/participants/${userId}/artifacts/${blockId}/versions`);
  }

  addParticipant(orgId: string, body: AddParticipantRequest): Observable<{ participant: Participant }> {
    return this.http.post<{ participant: Participant }>(`${this.base}/organizations/${orgId}/participants`, body);
  }

  bulkAddParticipants(
    orgId: string,
    rows: AddParticipantRequest[],
  ): Observable<BulkAddParticipantsResponse> {
    return this.http.post<BulkAddParticipantsResponse>(
      `${this.base}/organizations/${orgId}/participants/bulk`,
      { rows },
    );
  }

  // ---- Transition Roster (P2) ----

  private rosterBase(orgId: string, divisionId: string): string {
    return `${this.base}/organizations/${orgId}/divisions/${divisionId}`;
  }

  /** The division's flat seat list (paid inventory). */
  listSeats(orgId: string, divisionId: string): Observable<{ seats: Seat[] }> {
    return this.http.get<{ seats: Seat[] }>(`${this.rosterBase(orgId, divisionId)}/seats`);
  }

  /** Staff only (staff.comps). The API refuses claimed seats and removals without agreement. */
  amendSeatBundle(orgId: string, divisionId: string, seatId: string, body: AmendSeatBundleRequest): Observable<{ seat: Seat }> {
    return this.http.post<{ seat: Seat }>(`${this.base}/seat-bundles/${orgId}/${divisionId}/${seatId}`, body);
  }

  getSeat(orgId: string, divisionId: string, seatId: string): Observable<{ seat: Seat }> {
    return this.http.get<{ seat: Seat }>(`${this.rosterBase(orgId, divisionId)}/seats/${seatId}`);
  }

  listRenewalRequests(
    orgId: string,
    divisionId: string,
    status?: 'requested' | 'fulfilled' | 'declined',
  ): Observable<{ requests: RenewalRequest[] }> {
    const params = status ? new HttpParams().set('status', status) : new HttpParams();
    return this.http.get<{ requests: RenewalRequest[] }>(`${this.rosterBase(orgId, divisionId)}/renewal-requests`, { params });
  }

  declineRenewalRequest(
    orgId: string,
    divisionId: string,
    requestId: string,
    note?: string,
  ): Observable<{ request: RenewalRequest }> {
    return this.http.post<{ request: RenewalRequest }>(
      `${this.rosterBase(orgId, divisionId)}/renewal-requests/${requestId}/decline`,
      note ? { note } : {},
    );
  }

  /** Platform admin: enable / change / disable a division's self-serve enrollment link. */
  setDivisionEnrollment(
    orgId: string,
    divisionId: string,
    body: { enabled: boolean; productId?: string; slug?: string },
  ): Observable<{ division: Division }> {
    return this.http.patch<{ division: Division }>(`${this.rosterBase(orgId, divisionId)}/enrollment`, body);
  }

  /** The active catalog as this division sees it: public products plus its assigned restricted ones. */
  getDivisionCatalog(orgId: string, divisionId: string): Observable<{ packages: Product[]; addons: Product[] }> {
    return this.http.get<{ packages: Product[]; addons: Product[] }>(`${this.rosterBase(orgId, divisionId)}/catalog`);
  }

  /**
   * Renew a claimed seat for one more term at its original price (VOFF-268).
   * Returns a Stripe client secret in checkout mode, or the applied renewal
   * when invoiced or comped.
   */
  renewSeat(
    orgId: string,
    divisionId: string,
    seatId: string,
    body: {
      comp?: boolean;
      overrideSponsorship?: boolean;
      paymentMethodId?: string;
      /** The renewal purchase request a purchaser-only caller is approving (VOFF-360). */
      requestId?: string;
      /** One key per renewal attempt; a repeat returns the same renewal instead of extending twice. */
      requestKey?: string;
    } = {},
  ): Observable<RenewSeatResponse> {
    return this.http.post<RenewSeatResponse>(`${this.rosterBase(orgId, divisionId)}/seats/${seatId}/renew`, body);
  }

  // ---- the renewal purchase handoff: Enrollment asks, Purchasing commits (VOFF-360) ----

  /** Enrollment asks Purchasing to renew a seat for one more term. */
  requestRenewalPurchase(
    orgId: string,
    divisionId: string,
    seatId: string,
    body: { overrideSponsorship?: boolean; note?: string } = {},
  ): Observable<{ seat: Seat }> {
    return this.http.post<{ seat: Seat }>(
      `${this.rosterBase(orgId, divisionId)}/seats/${seatId}/renewal-purchase`,
      body,
    );
  }

  /** Enrollment takes back an open renewal request. */
  withdrawRenewalPurchase(orgId: string, divisionId: string, seatId: string): Observable<{ seat: Seat }> {
    return this.http.post<{ seat: Seat }>(
      `${this.rosterBase(orgId, divisionId)}/seats/${seatId}/renewal-purchase/withdraw`,
      {},
    );
  }

  /** A purchaser says no to a renewal request. */
  declineRenewalPurchase(
    orgId: string,
    divisionId: string,
    seatId: string,
    body: { requestId: string; note?: string },
  ): Observable<{ seat: Seat }> {
    return this.http.post<{ seat: Seat }>(
      `${this.rosterBase(orgId, divisionId)}/seats/${seatId}/renewal-purchase/decline`,
      body,
    );
  }

  /**
   * Cancel the open renewal payment for a seat, whether it failed or is
   * simply stuck, so it can start over. Enrollment or Purchasing. 409
   * `renewal_processing` while a bank debit is settling.
   */
  releaseRenewal(orgId: string, divisionId: string, seatId: string): Observable<{ seat: Seat }> {
    return this.http.post<{ seat: Seat }>(
      `${this.rosterBase(orgId, divisionId)}/seats/${seatId}/renewal/release`,
      {},
    );
  }

  createRoster(
    orgId: string,
    divisionId: string,
    body: { name?: string; defaultSponsorshipEndsAt?: string; defaultItems: SeatItemInput[]; seats?: RosterSeatInput[] },
  ): Observable<{ roster: TransitionRoster }> {
    return this.http.post<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters`,
      body,
    );
  }

  /** Every roster in a division that is not canceled, with seat counts. */
  listDivisionRosters(orgId: string, divisionId: string): Observable<{ rosters: RosterSummary[] }> {
    return this.http.get<{ rosters: RosterSummary[] }>(`${this.rosterBase(orgId, divisionId)}/rosters`, { params: { scope: 'all' } });
  }

  setRosterDefaultDate(orgId: string, divisionId: string, rosterId: string, sponsorshipEndsAt: string | null): Observable<{ roster: TransitionRoster }> {
    return this.http.patch<{ roster: TransitionRoster }>(`${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/default-date`, { sponsorshipEndsAt });
  }

  renameRoster(orgId: string, divisionId: string, rosterId: string, name: string): Observable<{ id: string; name: string }> {
    return this.http.patch<{ id: string; name: string }>(`${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/name`, { name });
  }

  /** In-progress rosters (building / awaiting_payment) for a division, newest first. */
  listInProgressRosters(
    orgId: string,
    divisionId: string,
  ): Observable<{ rosters: TransitionRoster[]; viewer: RosterViewer }> {
    return this.http.get<{ rosters: TransitionRoster[]; viewer: RosterViewer }>(
      `${this.rosterBase(orgId, divisionId)}/rosters`,
    );
  }

  getRoster(orgId: string, divisionId: string, rosterId: string): Observable<{ roster: TransitionRoster; viewer: RosterViewer }> {
    return this.http.get<{ roster: TransitionRoster; viewer: RosterViewer }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}`,
    );
  }

  setRosterDefaultPackage(
    orgId: string,
    divisionId: string,
    rosterId: string,
    items: SeatItemInput[],
  ): Observable<{ roster: TransitionRoster }> {
    return this.http.patch<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}`,
      { items },
    );
  }

  cancelRoster(orgId: string, divisionId: string, rosterId: string): Observable<{ roster: TransitionRoster }> {
    return this.http.post<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/cancel`,
      {},
    );
  }

  addRosterSeat(
    orgId: string,
    divisionId: string,
    rosterId: string,
    body: RosterSeatInput,
  ): Observable<{ roster: TransitionRoster }> {
    return this.http.post<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/seats`,
      body,
    );
  }

  addRosterSeatsBulk(
    orgId: string,
    divisionId: string,
    rosterId: string,
    rows: Array<{ email: string; name?: string; sponsorshipEndsAt?: string }>,
  ): Observable<BulkRosterSeatsResponse> {
    return this.http.post<BulkRosterSeatsResponse>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/seats/bulk`,
      { rows },
    );
  }

  /** Add N blank (open) seats for the shared-code path. */
  addRosterOpenSeats(
    orgId: string,
    divisionId: string,
    rosterId: string,
    count: number,
  ): Observable<{ roster: TransitionRoster }> {
    return this.http.post<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/seats/open`,
      { count },
    );
  }

  /** Send invitations to every named seat in the division not yet invited. */
  sendAllSeatInvites(
    orgId: string,
    divisionId: string,
  ): Observable<{ sent: number; skipped: number; errors: Array<{ seatId: string; email?: string; reason: string }> }> {
    return this.http.post<{ sent: number; skipped: number; errors: Array<{ seatId: string; email?: string; reason: string }> }>(
      `${this.rosterBase(orgId, divisionId)}/seats/send-all-invites`,
      {},
    );
  }

  updateRosterSeat(
    orgId: string,
    divisionId: string,
    rosterId: string,
    seatId: string,
    body: RosterSeatInput,
  ): Observable<{ roster: TransitionRoster }> {
    return this.http.patch<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/seats/${seatId}`,
      body,
    );
  }

  removeRosterSeat(
    orgId: string,
    divisionId: string,
    rosterId: string,
    seatId: string,
  ): Observable<{ roster: TransitionRoster }> {
    return this.http.delete<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/seats/${seatId}`,
    );
  }

  applyRosterDiscount(
    orgId: string,
    divisionId: string,
    rosterId: string,
    body: RosterDiscountInput,
  ): Observable<{ roster: TransitionRoster }> {
    return this.http.post<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/discount`,
      body,
    );
  }

  removeRosterDiscount(
    orgId: string,
    divisionId: string,
    rosterId: string,
  ): Observable<{ roster: TransitionRoster }> {
    return this.http.delete<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/discount`,
    );
  }

  checkoutRoster(
    orgId: string,
    divisionId: string,
    rosterId: string,
    body: { paymentMethodId?: string; expectedRevision?: number; requestId?: string } = {},
  ): Observable<CheckoutRosterResponse> {
    return this.http.post<CheckoutRosterResponse>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/checkout`,
      body,
    );
  }

  // ---- the purchase handoff: Enrollment asks, Purchasing commits (VOFF-359) ----

  /** Enrollment asks for the purchase of the roster as it stands. Never sends invitations. */
  requestPurchase(
    orgId: string,
    divisionId: string,
    rosterId: string,
    body: { expectedRevision?: number; note?: string } = {},
  ): Observable<{ roster: TransitionRoster }> {
    return this.http.post<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/purchase-request`,
      body,
    );
  }

  /** Enrollment takes back an open request. The roster stays a draft. */
  withdrawPurchaseRequest(orgId: string, divisionId: string, rosterId: string): Observable<{ roster: TransitionRoster }> {
    return this.http.post<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/purchase-request/withdraw`,
      {},
    );
  }

  /** A purchaser says no. Order view only, no participant names. */
  declinePurchaseRequest(
    orgId: string,
    divisionId: string,
    rosterId: string,
    body: { requestId: string; note?: string },
  ): Observable<{ roster: TransitionRoster }> {
    return this.http.post<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/purchase-request/decline`,
      body,
    );
  }

  /** "Purchases awaiting approval": open and in-progress requests across every division the caller may approve. */
  listAwaitingPurchases(orgId: string): Observable<{ requests: AwaitingPurchase[] }> {
    return this.http.get<{ requests: AwaitingPurchase[] }>(
      `${this.base}/organizations/${orgId}/purchase-requests`,
    );
  }

  /**
   * Cancel the payment in progress and put the roster back in `building` so
   * it can be changed and requested again. Enrollment Manage or Purchasing.
   * 409 `roster_payment_in_flight` while a bank debit is settling.
   */
  releaseRoster(orgId: string, divisionId: string, rosterId: string): Observable<{ roster: TransitionRoster }> {
    return this.http.post<{ roster: TransitionRoster }>(
      `${this.rosterBase(orgId, divisionId)}/rosters/${rosterId}/release`,
      {},
    );
  }

  // ---- saved payment methods on the org billing account (VOFF-299) ----

  getOrgBillingAccount(orgId: string, refresh = false): Observable<{ billingAccount: BillingAccountView | null }> {
    return this.http.get<{ billingAccount: BillingAccountView | null }>(
      `${this.base}/organizations/${orgId}/billing-account`,
      refresh ? { params: { refresh: '1' } } : {},
    );
  }

  createOrgSetupIntent(orgId: string): Observable<{ clientSecret: string; billingAccountId: string }> {
    return this.http.post<{ clientSecret: string; billingAccountId: string }>(
      `${this.base}/organizations/${orgId}/billing-account/setup-intent`,
      {},
    );
  }

  setOrgDefaultPaymentMethod(orgId: string, paymentMethodId: string): Observable<{ billingAccount: BillingAccountView }> {
    return this.http.post<{ billingAccount: BillingAccountView }>(
      `${this.base}/organizations/${orgId}/billing-account/default`,
      { paymentMethodId },
    );
  }

  setPaymentSourceSharing(orgId: string, paymentMethodId: string, body: { all: boolean; divisionIds: string[] }): Observable<{ billingAccount: BillingAccountView }> {
    return this.http.put<{ billingAccount: BillingAccountView }>(
      `${this.base}/organizations/${orgId}/billing-account/methods/${encodeURIComponent(paymentMethodId)}/sharing`,
      body,
    );
  }

  removeOrgPaymentMethod(orgId: string, paymentMethodId: string): Observable<{ billingAccount: BillingAccountView }> {
    return this.http.delete<{ billingAccount: BillingAccountView }>(
      `${this.base}/organizations/${orgId}/billing-account/methods/${paymentMethodId}`,
    );
  }

  /** Send (or re-send) a named seat's invitation email. */
  sendSeatInvite(orgId: string, divisionId: string, seatId: string): Observable<{ seat: Seat }> {
    return this.http.post<{ seat: Seat }>(
      `${this.rosterBase(orgId, divisionId)}/seats/${seatId}/send-invite`,
      {},
    );
  }

  /**
   * Edit a flattened inventory seat: reassign / change its email or name, or
   * clear the email (null/'') to release the seat back to open inventory. A
   * claimed seat is frozen (the API rejects with 409). (VOFF-94)
   */
  updateInventorySeat(
    orgId: string,
    divisionId: string,
    seatId: string,
    body: { email?: string | null; name?: string | null; sponsorshipEndsAt?: string | null },
  ): Observable<{ seat: Seat }> {
    return this.http.patch<{ seat: Seat }>(
      `${this.rosterBase(orgId, divisionId)}/seats/${seatId}`,
      body,
    );
  }

  /** The shared 6-digit redemption code for the division (generated on first read). */
  getDivisionCode(orgId: string, divisionId: string): Observable<{ code: string }> {
    return this.http.get<{ code: string }>(`${this.rosterBase(orgId, divisionId)}/code`);
  }

  // Public invitation endpoints — no admin auth required.
  getInvitation(token: string): Observable<InvitationContext> {
    return this.http.get<InvitationContext>(`${this.apiBase}/invitations/${token}`);
  }

  /** Create the invited account on the invite page (VOFF-404). The server takes the email from the invitation. */
  createInvitationAccount(token: string, password: string): Observable<{ ok: true }> {
    return this.http.post<{ ok: true }>(`${this.apiBase}/invitations/${token}/account`, { password });
  }

  acceptInvitation(token: string): Observable<AcceptInvitationResponse> {
    return this.http.post<AcceptInvitationResponse>(`${this.apiBase}/invitations/${token}/accept`, {});
  }

  resendAdminInvitation(orgId: string, invitationId: string): Observable<{ invitation: Invitation }> {
    return this.http.post<{ invitation: Invitation }>(`${this.base}/organizations/${orgId}/invitations/${invitationId}/resend`, {});
  }

  // Pricing catalog (platform-admin only) -------------------------------

  listProducts(filters: ListProductsFilters = {}): Observable<{ products: Product[] }> {
    let params = new HttpParams();
    if (filters.category) params = params.set('category', filters.category);
    if (filters.status) params = params.set('status', filters.status);
    return this.http.get<{ products: Product[] }>(`${this.base}/products`, { params });
  }

  getProduct(id: string): Observable<{ product: Product }> {
    return this.http.get<{ product: Product }>(`${this.base}/products/${id}`);
  }

  createProduct(body: CreateProductRequest): Observable<{ product: Product }> {
    return this.http.post<{ product: Product }>(`${this.base}/products`, body);
  }

  updateProduct(id: string, body: UpdateProductRequest): Observable<{ product: Product }> {
    return this.http.patch<{ product: Product }>(`${this.base}/products/${id}`, body);
  }

  archiveProduct(id: string): Observable<{ product: Product }> {
    return this.http.post<{ product: Product }>(`${this.base}/products/${id}/archive`, {});
  }

  unarchiveProduct(id: string): Observable<{ product: Product }> {
    return this.http.post<{ product: Product }>(`${this.base}/products/${id}/unarchive`, {});
  }

  // Catalog assignments for restricted products (platform-admin only) ----

  listProductAssignments(productId: string): Observable<{ assignments: CatalogAssignment[] }> {
    return this.http.get<{ assignments: CatalogAssignment[] }>(`${this.base}/products/${productId}/assignments`);
  }

  createProductAssignment(
    productId: string,
    body: { orgId: string; divisionId?: string },
  ): Observable<{ assignment: CatalogAssignment }> {
    return this.http.post<{ assignment: CatalogAssignment }>(`${this.base}/products/${productId}/assignments`, body);
  }

  deleteProductAssignment(productId: string, assignmentId: string): Observable<{ ok: true }> {
    return this.http.delete<{ ok: true }>(`${this.base}/products/${productId}/assignments/${assignmentId}`);
  }

  // Discount codes (staff.discounts) -------------------------------------

  listDiscountCodes(includeArchived = false): Observable<{ codes: DiscountCodeWithProduct[] }> {
    const params = includeArchived ? new HttpParams().set('includeArchived', 'true') : undefined;
    return this.http.get<{ codes: DiscountCodeWithProduct[] }>(`${this.base}/discount-codes`, { params });
  }

  getDiscountCode(code: string): Observable<{ code: DiscountCodeWithProduct; redemptions: DiscountRedemption[] }> {
    return this.http.get<{ code: DiscountCodeWithProduct; redemptions: DiscountRedemption[] }>(
      `${this.base}/discount-codes/${encodeURIComponent(code)}`,
    );
  }

  createDiscountCode(body: CreateDiscountCodeRequest): Observable<{ code: DiscountCode }> {
    return this.http.post<{ code: DiscountCode }>(`${this.base}/discount-codes`, body);
  }

  updateDiscountCode(code: string, body: UpdateDiscountCodeRequest): Observable<{ code: DiscountCode }> {
    return this.http.patch<{ code: DiscountCode }>(`${this.base}/discount-codes/${encodeURIComponent(code)}`, body);
  }

  archiveDiscountCode(code: string): Observable<{ code: DiscountCode }> {
    return this.http.post<{ code: DiscountCode }>(`${this.base}/discount-codes/${encodeURIComponent(code)}/archive`, {});
  }

  unarchiveDiscountCode(code: string): Observable<{ code: DiscountCode }> {
    return this.http.post<{ code: DiscountCode }>(`${this.base}/discount-codes/${encodeURIComponent(code)}/unarchive`, {});
  }

  // Inbox (staff.users.support, VOFF-284) ---------------------------------

  private inboxPath(scope: InboxScope, id?: string): string {
    const root = scope
      ? `${this.base}/organizations/${encodeURIComponent(scope.orgId)}${scope.divisionId ? `/divisions/${encodeURIComponent(scope.divisionId)}` : ''}/alerts`
      : `${this.base}/inbox/messages`;
    return id ? `${root}/${encodeURIComponent(id)}` : root;
  }

  listInboxMessages(scope: InboxScope = null): Observable<{ messages: InboxMessage[] }> {
    return this.http.get<{ messages: InboxMessage[] }>(this.inboxPath(scope));
  }

  getInboxMessage(id: string, scope: InboxScope = null): Observable<{ message: InboxMessage; stats: InboxStats }> {
    return this.http.get<{ message: InboxMessage; stats: InboxStats }>(this.inboxPath(scope, id));
  }

  createInboxDraft(body: InboxDraftBody, scope: InboxScope = null): Observable<{ message: InboxMessage }> {
    return this.http.post<{ message: InboxMessage }>(this.inboxPath(scope), body);
  }

  updateInboxDraft(id: string, body: InboxDraftBody, scope: InboxScope = null): Observable<{ message: InboxMessage }> {
    return this.http.put<{ message: InboxMessage }>(this.inboxPath(scope, id), body);
  }

  deleteInboxDraft(id: string, scope: InboxScope = null): Observable<{ ok: true }> {
    return this.http.delete<{ ok: true }>(this.inboxPath(scope, id));
  }

  /** Dry run: who the saved draft reaches. Sends nothing. */
  previewInboxAudience(id: string, scope: InboxScope = null): Observable<InboxAudiencePreview> {
    return this.http.post<InboxAudiencePreview>(`${this.inboxPath(scope, id)}/audience`, {});
  }

  /** The email copy as recipients would get it (VOFF-285). Sends nothing. */
  previewInboxEmail(id: string, scope: InboxScope = null): Observable<{ subject: string; html: string }> {
    return this.http.get<{ subject: string; html: string }>(`${this.inboxPath(scope, id)}/email-preview`);
  }

  sendInboxMessage(id: string, expectedCount: number, scope: InboxScope = null): Observable<{ message: InboxMessage }> {
    return this.http.post<{ message: InboxMessage }>(`${this.inboxPath(scope, id)}/send`, { expectedCount });
  }

  withdrawInboxMessage(id: string, scope: InboxScope = null): Observable<{ message: InboxMessage }> {
    return this.http.post<{ message: InboxMessage }>(`${this.inboxPath(scope, id)}/withdraw`, {});
  }

  // Feedback (platform-admin only) --------------------------------------

  listFeedback(params: ListFeedbackParams = {}): Observable<ListFeedbackResponse> {
    let httpParams = new HttpParams();
    if (params.limit) httpParams = httpParams.set('limit', String(params.limit));
    if (params.cursor) httpParams = httpParams.set('cursor', params.cursor);
    return this.http.get<ListFeedbackResponse>(`${this.base}/feedback`, { params: httpParams });
  }

  getFeedback(id: string): Observable<Feedback> {
    return this.http.get<Feedback>(`${this.base}/feedback/${id}`);
  }

  // ---- Participant files (VOFF-171) ----

  /** A participant's uploaded/generated files in Cloud Storage. */
  getUserFiles(userId: string): Observable<UserFilesResponse> {
    return this.http.get<UserFilesResponse>(`${this.base}/users/${userId}/files`);
  }

  /**
   * One participant file, through the audited staff endpoint (VOFF-348). Staff
   * listings carry no permanent link. Needs a live Sensitive data grant.
   */
  downloadUserFile(userId: string, path: string): Observable<Blob> {
    return this.http.get(`${this.base}/users/${userId}/files/content`, { params: { path }, responseType: 'blob' });
  }

  downloadOrgParticipantFile(orgId: string, userId: string, path: string): Observable<Blob> {
    return this.http.get(`${this.base}/organizations/${orgId}/participants/${userId}/files/content`, {
      params: { path },
      responseType: 'blob',
    });
  }

  // ---- Highlight reel (VOFF-165) ----
  //
  // An admin-facing, standalone curation of a participant's most meaningful
  // Scout interactions — a separate Claude call from the dossier, mirroring
  // its generate / getLatest / list-history lifecycle exactly.

  /** The latest saved highlight reel for a participant, or null if none generated yet. */
  getHighlights(userId: string): Observable<LatestHighlightsResponse> {
    return this.http.get<LatestHighlightsResponse>(`${this.base}/users/${userId}/highlights`);
  }

  /** Full highlight reel history, oldest first. */
  listHighlights(userId: string): Observable<HighlightsListResponse> {
    return this.http.get<HighlightsListResponse>(`${this.base}/users/${userId}/highlights/all`);
  }

  /**
   * Generate an AI highlight reel — a curated set of the participant's most
   * meaningful Scout moments, selected by a separate Claude call and rendered
   * verbatim. Persisted as a new history doc; can take 20-40 seconds.
   */
  generateHighlights(userId: string, programId?: string): Observable<HighlightsResponse> {
    return this.http.post<HighlightsResponse>(
      `${this.base}/users/${userId}/highlights`,
      programId ? { programId } : {},
    );
  }

  // ---- Org-scoped participant detail (VOFF-170) ----
  //
  // Mirror the platform /admin/users/:userId/* endpoints above, but scoped to
  // /admin/organizations/:orgId/participants/:userId/* so an org/division-tier
  // admin can read one of their own org's participants without the
  // platform-only endpoints' super-admin gate. Platform/super admins can read
  // any org through these too. Response shapes match the platform endpoints
  // 1:1, so the existing ProgressResponse/MemoriesResponse/ConversationsResponse/
  // LatestDossierResponse/DossierListResponse/DossierResponse types are reused
  // rather than duplicated.

  getScopedParticipantProgress(orgId: string, divisionId: string | null, offset = 0): Observable<ScopedProgressPage> {
    return this.http.get<ScopedProgressPage>(`${this.base}/organizations/${orgId}/participant-progress`, {
      params: { ...(divisionId ? { divisionId } : {}), offset, limit: 25 },
    });
  }

  getCustomerReport(orgId: string, query: CustomerReportQuery, participants = false): Observable<{ report: CustomerReport }> {
    return this.http.get<{ report: CustomerReport }>(`${this.base}/organizations/${orgId}/reports${participants ? '/participants' : ''}`, { params: { ...query } });
  }

  createCustomerInsight(orgId: string, query: CustomerReportQuery): Observable<CustomerInsight> {
    return this.http.post<CustomerInsight>(`${this.base}/organizations/${orgId}/reports/insights`, { tool: 'cohort_report', parameters: query });
  }

  getCustomerInsight(orgId: string, id: string): Observable<CustomerInsight> {
    return this.http.get<CustomerInsight>(`${this.base}/organizations/${orgId}/reports/insights/${encodeURIComponent(id)}`);
  }

  exportScopedParticipantProgress(orgId: string, divisionId: string | null): Observable<Blob> {
    return this.http.post(`${this.base}/organizations/${orgId}/participant-progress/export`, {}, {
      params: divisionId ? { divisionId } : {}, responseType: 'blob',
    });
  }

  getOrgParticipantProgress(
    orgId: string,
    userId: string,
    programId?: string,
  ): Observable<ProgressResponse> {
    let httpParams = new HttpParams();
    if (programId) httpParams = httpParams.set('programId', programId);
    return this.http.get<ProgressResponse>(
      `${this.base}/organizations/${orgId}/participants/${userId}/progress`,
      { params: httpParams },
    );
  }

  getOrgParticipantFiles(orgId: string, userId: string): Observable<ParticipantFilesResponse> {
    return this.http.get<ParticipantFilesResponse>(
      `${this.base}/organizations/${orgId}/participants/${userId}/files`,
    );
  }

  getOrgParticipantMemories(orgId: string, userId: string): Observable<MemoriesResponse> {
    return this.http.get<MemoriesResponse>(
      `${this.base}/organizations/${orgId}/participants/${userId}/memories`,
    );
  }

  getOrgParticipantConversations(orgId: string, userId: string): Observable<ConversationsResponse> {
    return this.http.get<ConversationsResponse>(
      `${this.base}/organizations/${orgId}/participants/${userId}/conversations`,
    );
  }

  /** The latest saved dossier for an org participant, or null if none generated yet. */
  getOrgParticipantDossier(orgId: string, userId: string): Observable<LatestDossierResponse> {
    return this.http.get<LatestDossierResponse>(
      `${this.base}/organizations/${orgId}/participants/${userId}/dossier`,
    );
  }

  /** Full dossier history for an org participant, oldest first. */
  listOrgParticipantDossiers(orgId: string, userId: string): Observable<DossierListResponse> {
    return this.http.get<DossierListResponse>(
      `${this.base}/organizations/${orgId}/participants/${userId}/dossiers`,
    );
  }

  /**
   * Generate an AI dossier for an org participant — same candid interpretive
   * read as the platform dossier, scoped to an org-tier caller.
   */
  generateOrgParticipantDossier(
    orgId: string,
    userId: string,
    programId?: string,
  ): Observable<DossierResponse> {
    return this.http.post<DossierResponse>(
      `${this.base}/organizations/${orgId}/participants/${userId}/dossier`,
      programId ? { programId } : {},
    );
  }

  /** The latest saved highlight reel for an org participant, or null if none yet. */
  getOrgParticipantHighlights(orgId: string, userId: string): Observable<LatestHighlightsResponse> {
    return this.http.get<LatestHighlightsResponse>(
      `${this.base}/organizations/${orgId}/participants/${userId}/highlights`,
    );
  }

  /** Full highlight reel history for an org participant, oldest first. */
  listOrgParticipantHighlights(orgId: string, userId: string): Observable<HighlightsListResponse> {
    return this.http.get<HighlightsListResponse>(
      `${this.base}/organizations/${orgId}/participants/${userId}/highlights/all`,
    );
  }

  /**
   * Generate an AI highlight reel for an org participant — the same curation
   * as the platform reel, scoped to an org-tier caller. Can take 20-40 seconds.
   */
  generateOrgParticipantHighlights(
    orgId: string,
    userId: string,
    programId?: string,
  ): Observable<HighlightsResponse> {
    return this.http.post<HighlightsResponse>(
      `${this.base}/organizations/${orgId}/participants/${userId}/highlights`,
      programId ? { programId } : {},
    );
  }

  // ---- Admin Analyst (Scout chat, role-derived scope) ----

  /** The caller's own chats — no orgId param; the server resolves whose chats
   *  these are (and their scope) from the caller's role. */
  listAnalystChats(): Observable<ListAnalystChatsResponse> {
    return this.http.get<ListAnalystChatsResponse>(`${this.base}/analytics/analyst/chats`);
  }

  /** Turn a research pseudonym into a reason-logged participant session. */
  resolveResearchPseudonym(body: ResolveResearchPseudonymBody): Observable<ResolveResearchPseudonymResponse> {
    return this.http.post<ResolveResearchPseudonymResponse>(`${this.base}/research/resolve`, body);
  }

  getAnalystChat(chatId: string): Observable<GetAnalystChatResponse> {
    return this.http.get<GetAnalystChatResponse>(`${this.base}/analytics/analyst/chats/${chatId}`);
  }

  /**
   * Stream one analyst turn. fetch()-based (not HttpClient), so the auth
   * interceptor never sees it — attaches the bearer token itself via
   * AuthService before opening the stream, same reason as vo-user's Career
   * Studio tools.
   */
  streamAnalystMessage(
    body: StreamAnalystMessageBody,
    handlers: {
      onDelta?: (text: string) => void;
      onEvent?: (event: string, data: Record<string, unknown>) => void;
    } = {},
  ): Promise<StreamAnalystMessageResult> {
    return this.streamSse<StreamAnalystMessageResult>(
      '/admin/analytics/analyst/message/stream',
      body,
      handlers,
    );
  }

  /**
   * Generic SSE stream consumer. Forwards `delta` events' text to `onDelta`
   * and any other named event (e.g. this endpoint's `meta`/`reset`) to
   * `onEvent`, throws on `error`, and resolves with the `done` event payload.
   * Copied verbatim from vo-user's api.service.ts streamSse (Career Pathfinder
   * tools) — frame-split on a blank line, incremental UTF-8 decode.
   */
  private async streamSse<T>(
    path: string,
    body: unknown,
    handlers: {
      onDelta?: (text: string) => void;
      onEvent?: (event: string, data: Record<string, unknown>) => void;
    } = {},
  ): Promise<T> {
    const token = await this.authService.getIdToken();
    const res = await fetch(`${environment.apiUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body ?? {}),
    });
    if (!res.ok || !res.body) throw new Error(`Stream failed (${res.status})`);

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: T | null = null;
    let streamError: string | null = null;

    const handleFrame = (frame: string): void => {
      let event = 'message';
      const dataLines: string[] = [];
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
      }
      let data: Record<string, unknown> = {};
      const dataStr = dataLines.join('\n');
      if (dataStr) {
        try {
          data = JSON.parse(dataStr) as Record<string, unknown>;
        } catch {
          data = {};
        }
      }
      if (event === 'delta') {
        const t = data['text'];
        if (typeof t === 'string') handlers.onDelta?.(t);
      } else if (event === 'done') {
        result = data as unknown as T;
      } else if (event === 'error') {
        const e = data['error'];
        streamError = typeof e === 'string' ? e : 'Stream error';
      } else {
        handlers.onEvent?.(event, data);
      }
    };

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        if (frame.trim()) handleFrame(frame);
      }
    }
    buffer += decoder.decode(); // flush any dangling multi-byte sequence
    if (buffer.trim()) handleFrame(buffer);

    if (streamError) throw new Error(streamError);
    if (!result) throw new Error('Stream ended without a result');
    return result;
  }
}

export interface InvitationContext {
  invitation: Invitation;
  organization: { id: string; name: string; status: string };
  /** `new` when the invited email has no usable account yet, so the page offers to create one. */
  account?: 'new' | 'existing';
}

export interface AcceptInvitationResponse {
  organization: { id: string; name: string; status: string };
  invitation: Invitation;
  roleAssignment?: { id: string; userId: string; orgId: string; role: string };
  userId: string;
  email: string;
}

export interface ScopedProgressRow {
  userId: string; name: string; email: string;
  runId: string; runName: string; programId: string; divisionId: string;
  status: string; lastActive: 'within_week' | 'within_month' | 'older' | 'unknown';
  modules: Array<{ id: string; title: string; steps: Array<{ id: string; title: string; completed: boolean }> }>;
}
export interface ScopedProgressPage { level: 'progress'; participants: ScopedProgressRow[]; nextOffset: number | null }
export interface CustomerReportQuery { divisionId?: string; programId?: string; cohortMonth?: string; metric: 'completion' | 'activity' | 'required_answers' | 'optional_answers'; window: '7_days' | '30_days' }
export interface CustomerReport {
  policyVersion: string; metric: string; window: string; asOf: string; cohortSize: number;
  cells: Array<{ label: string; count: number | null }>; notice: string;
  definitions: { denominator: string; completion: string; activity: string; exclusions: string; answerMilestones?: string };
  participants?: Array<{ userId: string; name: string; email: string; matchesMetric: boolean; metricState?: 'matches' | 'does_not_match' | 'not_applicable' | 'unknown' }>;
}
export interface CustomerInsight { id: string; policyVersion: string; request: { tool: 'cohort_report'; parameters: CustomerReportQuery }; result: { kind: 'cohort_report'; report: CustomerReport } }

export interface ProgressResponse {
  moduleCompletion?: Record<string, SharedContentProgress>;
  completion?: SharedContentProgress | null;
  /** Content resolved to the selected run or global container version. */
  program?: Program | null;
  programId: string;
  progress: ProgramProgress | null;
}

export interface ConversationWithMessages {
  conversation: Conversation;
  messages: Message[];
}

export interface ConversationsResponse {
  conversations: ConversationWithMessages[];
}

/** A dashboard Scout chat, with the workspace it began in. */
export interface AdminScoutChat extends ConversationWithMessages {
  workspaceKey: string;
  workspaceName: string;
}

export interface AdminInterviewMessage {
  id: string;
  interviewId: string;
  role: 'user' | 'assistant';
  /** HTML for assistant messages, plain text for user messages. */
  content: string;
  messageType?: 'interviewer' | 'tip' | 'warning' | 'howto' | 'assessment';
  phase?: string;
  timestamp: string;
}

export interface AdminInterview {
  id: string;
  difficulty: 'warmup' | 'realistic' | 'hard';
  phase: string;
  status: 'active' | 'complete';
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  job: { jobPostingId?: string; title: string; company: string; location: string };
  scorecard?: { overall: string; readiness: number };
}

export interface OtherChatsResponse {
  foundation: ConversationWithMessages[];
  dashboard: AdminScoutChat[];
  interviews: { interview: AdminInterview; messages: AdminInterviewMessage[] }[];
}

export interface MemoriesResponse {
  memories: Memory[];
}

export interface DecisionsResponse {
  decisions: Decision[];
}

export interface ArtifactsResponse {
  artifacts: StoredArtifact[];
}

export interface ArtifactVersionsResponse {
  versions: StoredArtifact[];
}

export interface GeneratedDossier {
  /** Firestore doc id within users/{uid}/dossiers. */
  id: string;
  /** Semantic HTML, rendered directly in the dossier modal. */
  content: string;
  model: string;
  programId: string;
  generatedAt: string;
  generatedByUid?: string;
  generatedByEmail?: string;
}

export interface DossierResponse {
  dossier: GeneratedDossier;
}

export interface LatestDossierResponse {
  dossier: GeneratedDossier | null;
}

export interface DossierListResponse {
  /** Full dossier history, oldest first. */
  dossiers: GeneratedDossier[];
}

export type ResetScope = 'step' | 'module' | 'program' | 'memories' | 'all';

export interface ResetRequest {
  scope: ResetScope;
  programId?: string;
  moduleId?: string;
  stepId?: string;
  confirmation: string;
  /** Why the reset is needed (10 to 500 characters). Recorded before anything is deleted. */
  reason: string;
}

export interface ResetResponse {
  ok: boolean;
  scope: ResetScope;
  deletedCounts: Record<string, number>;
}

// ---- Participant files (VOFF-171) ----

export interface UserFilesResponse {
  files: UserFile[];
}

// ---- Highlight reel (VOFF-165) ----
//
// Mirrors vo-api's src/types/highlights.ts (HighlightMomentType,
// HighlightMoment, StoredHighlightReel) exactly. Duplicated here rather than
// shared because vo-admin's @app/types barrel doesn't cross the API
// boundary — keep these in sync if the backend shape ever changes.

export type HighlightMomentType = 'learning' | 'vulnerability' | 'lightbulb' | 'growth' | 'decision';

/**
 * One surrounding turn carried with a moment for context. Verbatim, like the
 * anchor pair. `role` says whether `content` is plain participant text or
 * Scout's safe HTML.
 */
export interface HighlightTurn {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

/** One selected moment: a participant message paired with the Scout reply that followed it. */
export interface HighlightMoment {
  /** Why this moment stands out. */
  momentType: HighlightMomentType;
  /** One plain line saying why the moment matters. Written by the model. */
  caption: string;
  /** The module this exchange was stamped to, when known. */
  moduleId?: string;
  /** The step this exchange was stamped to, when known. */
  stepId?: string;
  /** Human-readable module title, resolved from the program, when known. */
  moduleTitle?: string;
  /** Related lead-in turns before the anchor, chronological. Verbatim. */
  context?: HighlightTurn[];
  /** The participant's message, verbatim. Plain text. */
  userMessage: { id: string; content: string; timestamp: string };
  /** Scout's reply, verbatim. Safe HTML. */
  scoutMessage: { id: string; content: string; timestamp: string };
  /** Related follow-up turns after the anchor, chronological. Verbatim. */
  followUp?: HighlightTurn[];
}

/** A generated highlight reel, persisted under users/{uid}/highlights/{id}. */
export interface StoredHighlightReel {
  /** Firestore doc id within users/{uid}/highlights. */
  id: string;
  userId: string;
  programId: string;
  /** The model that produced the selection, surfaced for transparency. */
  model: string;
  generatedAt: string;
  generatedByUid?: string;
  generatedByEmail?: string;
  /** The selected moments, in chronological order. May be empty. */
  highlights: HighlightMoment[];
  /** Semantic HTML for display/export — a graceful fallback message when `highlights` is empty. */
  content: string;
}

export interface HighlightsResponse {
  reel: StoredHighlightReel;
}

export interface LatestHighlightsResponse {
  reel: StoredHighlightReel | null;
}

export interface HighlightsListResponse {
  reels: StoredHighlightReel[];
}

/** A participant's uploaded files (org-scoped detail — VOFF-170). */
export interface ParticipantFilesResponse {
  files: UserFile[];
}

/**
 * One of a participant's runs, as the admin API returns it. Real library
 * programs only — Program Foundation and Profile are global content, not
 * runs, and never appear here (the separate `onboarding` field on
 * AdminRunsResponse carries Program Foundation's own state).
 */
export interface AdminRun {
  id: string;
  programId: string;
  programTitle: string;
  displayName: string;
  status: 'active' | 'completed' | 'archived';
  enrolledAt: string;
  completedAt?: string;
  completion: {
    requiredDone: number;
    requiredTotal: number;
    allDone: number;
    allTotal: number;
    requiredSatisfied: boolean;
    percent: number;
  } | null;
}

export interface AdminRunsResponse {
  runs: AdminRun[];
  onboarding: SharedContentProgress | null;
  profile: SharedContentProgress | null;
  profileInfo: UserProfile | null;
}


// ── Scout Settings types (mirror vo-api types/scout-policy.ts, org-resource.ts) ──

export type GuidanceSection = 'about' | 'voice' | 'evaluation' | 'artifacts' | 'routing';
export const GUIDANCE_SECTIONS: readonly GuidanceSection[] = ['about', 'voice', 'evaluation', 'artifacts', 'routing'];

export interface OrgScoutPolicyContent {
  sections: Partial<Record<GuidanceSection, string>>;
  locks: Partial<Record<GuidanceSection | 'includeFoundation', boolean>>;
  includeFoundation: boolean;
}

export interface DivisionScoutPolicyContent {
  sections: Partial<Record<GuidanceSection, { text: string; mode: 'append' | 'replace' }>>;
  includeFoundation?: boolean;
}

export interface ScoutPolicyState<C> {
  draft: C;
  draftRev: number;
  draftUpdatedAt?: string;
  draftUpdatedBy?: string;
  publishedVersion: number | null;
}

export interface ScoutPolicyVersionMeta { version: number; publishedAt: string; publishedBy: string; notes?: string }

export interface ScoutSectionInfo { label: string; help: string; example: string }

export interface EffectiveScoutPolicy {
  orgId: string;
  divisionId: string | null;
  versions: { organization: number | 'draft' | null; division: number | 'draft' | null };
  sections: Record<GuidanceSection, { locked: boolean; parts: Array<{ source: 'organization' | 'division'; text: string }>; divisionMode?: 'append' | 'replace' }>;
  includeFoundation: { value: boolean; source: 'platform' | 'organization' | 'division'; locked: boolean };
  advisories: Array<{ section: GuidanceSection | 'includeFoundation'; level: 'info' | 'warning'; message: string }>;
}

export interface ScoutPolicyResponse extends ScoutPolicyState<OrgScoutPolicyContent & DivisionScoutPolicyContent> {
  versions: ScoutPolicyVersionMeta[];
  sectionInfo: Record<GuidanceSection, ScoutSectionInfo>;
  /** Division pages only: what the organization has published. */
  inherited?: EffectiveScoutPolicy;
}

export interface ScoutCitation { resourceId: string; title: string; version: number; page: number | null; section: string | null; listed: boolean }

export interface ScoutPreviewResult {
  baseline: { reply: string; model: string };
  withGuidance: { reply: string; model: string; citations: ScoutCitation[]; versions: EffectiveScoutPolicy['versions'] };
  usedDrafts: boolean;
}

export type ResourceAudience = 'participants' | 'scout' | 'admins';

export interface OrgResource {
  id: string;
  orgId: string;
  owner: { level: 'organization' } | { level: 'division'; divisionId: string };
  divisionIds: string[];
  title: string;
  whenToUse: string;
  audience: ResourceAudience;
  kind: 'document' | 'text' | 'link';
  url?: string;
  status: 'processing' | 'active' | 'failed' | 'withdrawn';
  currentVersion: number | null;
  latestVersion: number;
  latestAttempt?: { version: number; status: 'active' | 'failed'; failureReason?: string; at: string };
  effectiveFrom?: string;
  expiresAt?: string;
  priority: number;
  updatedAt: string;
  /** False when the item belongs to the organization and this is a division page. */
  manageable?: boolean;
}

export interface OrgResourceVersion {
  version: number;
  status: 'active' | 'failed';
  originalName?: string;
  size?: number;
  textChars?: number;
  extraction?: 'text' | 'ocr';
  failureReason?: string;
  uploadedAt: string;
  indexStatus?: 'indexed' | 'failed';
  chunkCount?: number;
  indexError?: string;
}

export interface OrgContact {
  id: string;
  owner: { level: 'organization' } | { level: 'division'; divisionId: string };
  divisionIds: string[];
  name: string;
  role?: string;
  email?: string;
  phone?: string;
  url?: string;
  helpsWith: string;
  audience: 'participants' | 'admins';
  status: 'active' | 'archived';
  sortOrder: number;
  manageable?: boolean;
}

// ── Branding types (white-label T28, mirror vo-api branding) ──
export interface BrandingLocks { displayName?: boolean; logo?: boolean; colors?: boolean; css?: boolean }
export interface BrandingDoc {
  displayName?: string;
  logo?: { path: string; alt: string; contentType: string };
  colors?: { primary?: string; accent?: string };
  css?: string;
  locks?: BrandingLocks;
  revision: number;
  updatedAt: string;
  updatedBy: string;
}
export interface BrandingPalette { primary: string; primaryHover: string; onPrimary: string; primaryText: string; accent: string; accentHover: string; onAccent: string }
export interface EffectiveBranding {
  key: string;
  source: 'ventureoff' | 'organization' | 'division';
  displayName: string;
  logo: { url: string; alt: string; contentType: string } | null;
  theme: { light: BrandingPalette; dark: BrandingPalette } | null;
  css: string | null;
  poweredBy: true;
  adjusted: string[];
}
export interface BrandingResponse {
  branding: BrandingDoc | null;
  effective: EffectiveBranding;
  canEditCss: boolean;
  organizationLocks?: BrandingLocks;
  organization?: BrandingDoc | null;
}
export interface BrandingUpdate {
  displayName?: string | null;
  logo?: { path: string; alt: string } | null;
  colors?: { primary?: string | null; accent?: string | null } | null;
  css?: string | null;
  locks?: BrandingLocks;
}

// ── Messages types (white-label T29, mirror vo-api communications) ──
export type MessageKind = 'invitation' | 'welcome' | 'accessEnding' | 'accessEnded';
export interface MessageCopy { subject?: string; heading?: string; body?: string; enabled?: boolean }
export interface SupportContacts { programContactName?: string; programContactEmail?: string; refundsEmail?: string }
export type CommunicationLocks = Partial<Record<MessageKind | 'support', boolean>>;
export interface CommunicationsDoc {
  messages?: Partial<Record<MessageKind, MessageCopy>>;
  support?: SupportContacts;
  locks?: CommunicationLocks;
  revision: number;
  updatedAt: string;
  updatedBy: string;
}
export type CommunicationSource = 'ventureoff' | 'organization' | 'division';
export interface EffectiveCommunications {
  messages: Record<MessageKind, { copy: { subject: string; heading: string; body: string; enabled?: boolean }; source: CommunicationSource }>;
  support: SupportContacts & { source: CommunicationSource };
}
export interface CommunicationsResponse {
  communications: CommunicationsDoc | null;
  effective: EffectiveCommunications;
  variables: Record<MessageKind, string[]>;
  ventureoffSupport: string;
  organizationLocks?: CommunicationLocks;
  organization?: CommunicationsDoc | null;
}
export interface CommunicationsUpdate {
  messages?: Partial<Record<MessageKind, MessageCopy | null>>;
  support?: SupportContacts | null;
  locks?: CommunicationLocks;
}
export interface CommunicationPreviewRequest { kind: MessageKind; copy?: MessageCopy; support?: SupportContacts }
export interface CommunicationPreview { kind: MessageKind; subject: string; html: string; text: string; senderName: string; replyTo: string; source: CommunicationSource }

// ── Setup checklist and effective settings types (white-label T30) ──
export type SetupStatus = 'done' | 'todo' | 'optional' | 'attention';
export interface SetupItem { id: string; title: string; status: SetupStatus; detail: string; path: string; required: boolean; owner: 'customer' | 'ventureoff' }
export interface SetupChecklist { orgId: string; ready: boolean; items: SetupItem[]; divisions: { divisionId: string; name: string; items: SetupItem[] }[] }

export type SettingSource = 'ventureoff' | 'organization' | 'division';
export interface EffectiveSettingsProgram {
  programId: string; title: string; owner: SettingSource; source: 'platform' | 'organization' | 'division';
  order?: number; featured?: boolean;
  requirements?: ({ kind: 'program'; programId: string; title: string } | { kind: 'profile-answered'; title: string })[];
  issues?: { code: string; requirementId?: string; message: string }[];
}
export type GuidanceSectionKey = 'about' | 'voice' | 'evaluation' | 'artifacts' | 'routing';
export interface EffectiveGuidance {
  sections?: Partial<Record<GuidanceSectionKey, { locked?: boolean; parts?: { source: string; text: string }[] }>>;
  advisories?: { section?: string; level?: string; message: string }[];
}
export interface EffectiveSettings {
  scope: { orgId: string; orgName: string; divisionId: string; divisionName: string };
  library?: EffectiveSettingsProgram[];
  guidance?: EffectiveGuidance;
  references?: {
    resources?: { id: string; title: string; audience: 'participants' | 'scout' | 'admins'; status?: string; source: string }[];
    contacts?: { id: string; name: string; role?: string; source: string }[];
  };
  branding?: { effective: EffectiveBranding; sources?: { displayName?: SettingSource; logo?: SettingSource; colors?: SettingSource; css?: SettingSource } };
  messages?: Partial<Record<MessageKind, { source: string; subject?: string; enabled?: boolean }>>;
  support?: { programContactName?: string; programContactEmail?: string; refundsEmail?: string; source?: string };
  seats?: { total: number; open: number; invited: number; claimed: number; code: boolean };
  curriculumAuthors?: { id: string; userId: string; scope: 'organization' | 'division'; grantedAt?: string }[];
}
